/* AG Admin Shipping, operator UI for the Shipping Calculator.
 *
 * Tabs:
 *   - Test calculator (synthetic quote tester at top, runs against live calc)
 *   - Service tiers (Standard / Express / Next Day…)
 *   - Parcel rates (weight×zone matrix)
 *   - Pallet rates (weight×zone matrix)
 *   - Zones (postcode prefix → zone)
 *   - Free shipping (rules)
 *   - SKU overrides (per-SKU flat fees / extras / pallet-only flags)
 *   - Config (general)
 *   - Logs (last 30 quote requests)
 *
 * Talks to ag-shipping-script /exec via window.AG_ADMIN.api (provided
 * by AG-Admin-Shell). Backend lives at github.com/AssassinGoods/ag-shipping-script.
 */
(function () {
  'use strict';
  var $ = function (id) { return document.getElementById(id); };
  function esc(s){ return String(s == null ? '' : s).replace(/[&<>"]/g, function(c){ return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]; }); }
  function fmtMoney(n) { var x = parseFloat(n) || 0; return '€' + x.toFixed(2); }
  function fmtDate(s) { return String(s || '').slice(0, 10); }

  /* Tab grouping: 16 flat tabs is overwhelming for non-technical staff.
     Groups: Daily (test + logs + help, what colleagues touch most),
     Pricing (rates), Products (carrier assignment), Setup (rarely-edited
     plumbing), Tools (one-off ops). The nav renders as a 2-row strip:
     groups above, current group's tabs below. */
  var TAB_GROUPS = [
    {
      id: 'daily',
      label: 'Daily',
      hint: 'What you check most: test quotes, recent customer requests, reference docs.',
      tabs: ['test', 'logs', 'info']
    },
    {
      id: 'pricing',
      label: 'Pricing',
      hint: 'The money: rates, surcharges, free-shipping rules.',
      tabs: ['rates', 'pallets', 'surcharges', 'free']
    },
    {
      id: 'products',
      label: 'Products',
      hint: 'Which courier ships which product, per-SKU rule overrides.',
      tabs: ['products', 'overrides']
    },
    {
      id: 'setup',
      label: 'Setup',
      hint: 'Plumbing: couriers, speed options, zones, closed days, config.',
      tabs: ['carriers', 'buckets', 'meta', 'zones', 'closed', 'config']
    },
    {
      id: 'tools',
      label: 'Tools',
      hint: 'Snapshots, CSV import/export, audit log.',
      tabs: ['tools']
    }
  ];

  var TABS = [
    { id: 'test',       label: 'Test calculator',  group: 'daily',    icon: '🧪' },
    { id: 'logs',       label: 'Recent quotes',    group: 'daily',    icon: '📋' },
    { id: 'info',       label: 'How it works',     group: 'daily',    icon: '📖' },
    { id: 'rates',      label: 'Parcel rates',     group: 'pricing',  icon: '€' },
    { id: 'pallets',    label: 'Pallet rates',     group: 'pricing',  icon: '€' },
    { id: 'surcharges', label: 'Surcharges',       group: 'pricing',  icon: '€' },
    { id: 'free',       label: 'Free shipping',    group: 'pricing',  icon: '€' },
    { id: 'products',   label: 'Product carriers', group: 'products', icon: '📦' },
    { id: 'overrides',  label: 'SKU overrides',    group: 'products', icon: '🏷' },
    { id: 'carriers',   label: 'Carriers',         group: 'setup',    icon: '🚚' },
    { id: 'buckets',    label: 'Speed buckets',    group: 'setup',    icon: '⏱' },
    { id: 'meta',       label: 'Carrier × bucket', group: 'setup',    icon: '⏱' },
    { id: 'zones',      label: 'Zones',            group: 'setup',    icon: '🗺' },
    { id: 'closed',     label: 'Closed days',      group: 'setup',    icon: '📅' },
    { id: 'config',     label: 'Config',           group: 'setup',    icon: '⚙' },
    { id: 'tools',      label: 'Tools',            group: 'tools',    icon: '🛠' }
  ];
  function tabsForGroup(groupId) { return TABS.filter(function(t) { return t.group === groupId; }); }
  function groupForTab(tabId) {
    var t = TABS.filter(function(x) { return x.id === tabId; })[0];
    return t ? t.group : 'daily';
  }

  var state = {
    tab: 'test',
    group: 'daily',
    payload: null,
    testItems: [{ sku: '', quantity: 1, grams: 1000, price_pence: 5000 }],
    testPostcode: 'CA11 9DP',
    testCountry: 'GB',
    testRequestedShipDate: ''
  };

  /* Dirty-row tracker. Keyed by sheet name + _row so it survives tab
     switches and table-level repaints. Wired below into:
       - input/change handlers (mark dirty)
       - per-row Save + Save all (mark clean on success)
       - window.beforeunload (block navigation if dirty)
     A single global object is plenty, there's only ever one operator
     editing at a time. */
  var dirty = { rows: {}, nextNewId: 1 }; // { "<sheetKey>:<rowNum|new-N>": { ref, saveAction, headers } }
  function dirtyKey(sheetKey, row) {
    /* Saved rows key on sheet row number. New (unsaved) rows get a stable
       per-session id stashed on the row so re-editing the same blank row
       updates one entry rather than creating a new one each keystroke. */
    if (row._row) return sheetKey + ':' + row._row;
    if (!row.__dirtyNewId) row.__dirtyNewId = 'new-' + (dirty.nextNewId++);
    return sheetKey + ':' + row.__dirtyNewId;
  }
  function markDirty(sheetKey, row, headers, saveAction) {
    dirty.rows[dirtyKey(sheetKey, row)] = { ref: row, saveAction: saveAction, headers: headers, sheetKey: sheetKey };
    refreshDirtyUi();
  }
  function markClean(sheetKey, row) {
    /* A just-saved new row has BOTH __dirtyNewId (old key) and _row
       (new key). Delete both possible keys so the entry can't linger
       and trigger spurious "unsaved changes" prompts. */
    if (row.__dirtyNewId) delete dirty.rows[sheetKey + ':' + row.__dirtyNewId];
    if (row._row) delete dirty.rows[sheetKey + ':' + row._row];
    refreshDirtyUi();
  }
  function dirtyCount() { return Object.keys(dirty.rows).length; }
  function findRowTr_(rowRef) {
    var current = window.__currentTableRows || [];
    var matchedIdx = -1;
    for (var i = 0; i < current.length; i++) {
      if (current[i] === rowRef) { matchedIdx = i; break; }
    }
    if (matchedIdx < 0) return null;
    var trs = document.querySelectorAll('#ship-table-body tr');
    for (var j = 0; j < trs.length; j++) {
      var firstInput = trs[j].querySelector('[data-row-idx]');
      if (firstInput && parseInt(firstInput.dataset.rowIdx, 10) === matchedIdx) return trs[j];
    }
    return null;
  }

  function refreshDirtyUi() {
    var n = dirtyCount();
    /* Per-tab Save bar (in-table, appears alongside Add row). The
       button starts with an inner <span id=ship-dirty-count>; my first
       textContent assignment destroyed that span and broke subsequent
       lookups. Gate only on the button itself. */
    var inlineBtn = document.getElementById('ship-save-all');
    if (inlineBtn) {
      inlineBtn.textContent = n > 0 ? ('Save ' + n + ' change' + (n === 1 ? '' : 's')) : 'All saved ✓';
      inlineBtn.disabled = n === 0;
      inlineBtn.style.opacity = n === 0 ? '0.55' : '';
    }
    /* Persistent header status pill (top of every tab). */
    var hdrPill = document.getElementById('ship-status-saved');
    if (hdrPill) {
      if (n === 0) {
        hdrPill.textContent = 'All changes saved';
        hdrPill.style.color = '#2a7a2a';
        hdrPill.style.background = '#eaf6ea';
        hdrPill.style.borderColor = '#bfe0bf';
      } else {
        hdrPill.textContent = n + ' unsaved change' + (n === 1 ? '' : 's');
        hdrPill.style.color = '#a45200';
        hdrPill.style.background = '#fff4e3';
        hdrPill.style.borderColor = '#ffd28a';
      }
    }
    /* Tint dirty rows so the operator can see at a glance which rows
       still need saving. Clear any non-flash backgrounds first. */
    document.querySelectorAll('#ship-table-body tr').forEach(function(tr) {
      if (tr.style.background === 'rgb(255, 251, 234)') tr.style.background = '';
    });
    Object.values(dirty.rows).forEach(function(entry) {
      var tr = findRowTr_(entry.ref);
      if (tr && !tr.style.background) tr.style.background = '#fffbea';
    });
  }
  window.addEventListener('beforeunload', function(e) {
    if (dirtyCount() === 0) return;
    e.preventDefault();
    e.returnValue = '';
    return '';
  });

  window.AG_ADMIN.onReady(function () {
    boot();
  });

  function boot() {
    injectCss();
    state.group = groupForTab(state.tab);
    renderTabs();
    refresh();
  }

  /* Inject styling once, on boot. Keeps row-level renderers free of
     inline-style spam and centralises the visual language. */
  function injectCss() {
    if (document.getElementById('ag-shipping-styles')) return;
    var css = ''
      + '.ag-ship-status{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:10px 14px;background:#fff;border:1px solid #e6e6e6;border-radius:6px;margin-bottom:10px;flex-wrap:wrap;font-size:12px;}'
      + '.ag-ship-status .pill{display:inline-flex;align-items:center;gap:6px;padding:3px 10px;border-radius:999px;border:1px solid;font-weight:600;font-size:11px;letter-spacing:0.02em;}'
      + '.ag-ship-status .pill.active{color:#2a7a2a;background:#eaf6ea;border-color:#bfe0bf;}'
      + '.ag-ship-status .pill.inactive{color:#a02020;background:#fdecec;border-color:#f0bdbd;}'
      + '.ag-ship-status .pill.warn{color:#a45200;background:#fff4e3;border-color:#ffd28a;}'
      + '.ag-ship-status .pill.muted{color:#666;background:#f4f4f4;border-color:#ddd;}'
      + '.ag-ship-status .dot{display:inline-block;width:8px;height:8px;border-radius:50%;}'
      + '.ag-ship-group-row{display:flex;gap:4px;border-bottom:1px solid #e6e6e6;padding:0 0 6px;margin-bottom:6px;flex-wrap:wrap;}'
      + '.ag-ship-group{padding:8px 14px;background:transparent;border:1px solid transparent;border-radius:6px 6px 0 0;cursor:pointer;font-size:13px;font-weight:600;color:#555;}'
      + '.ag-ship-group:hover{color:#c8102e;background:#fafafa;}'
      + '.ag-ship-group.active{color:#c8102e;background:#fff;border-color:#e6e6e6;border-bottom-color:#fff;margin-bottom:-1px;}'
      + '.ag-ship-tab-row{display:flex;gap:4px;padding:0 0 12px;flex-wrap:wrap;}'
      + '.ag-ship-tab{padding:6px 12px;background:#f4f4f4;border:1px solid #e6e6e6;border-radius:4px;cursor:pointer;font-size:12px;color:#555;display:inline-flex;align-items:center;gap:6px;}'
      + '.ag-ship-tab:hover{background:#fff;color:#c8102e;border-color:#c8102e;}'
      + '.ag-ship-tab.active{background:#c8102e;color:#fff;border-color:#c8102e;}'
      + '.ag-ship-tab .ico{font-size:13px;line-height:1;}'
      + '.ag-ship-group-hint{font-size:11px;color:#888;padding:0 4px 8px;}'
      + '.ag-ship-intro{background:#fafafa;border:1px solid #e6e6e6;border-left:3px solid #c8102e;border-radius:4px;padding:10px 14px;font-size:12.5px;line-height:1.5;color:#444;margin:0 0 14px;}'
      + '.ag-ship-intro strong{color:#222;}'
      + 'table.ag-table tbody tr:nth-child(even):not([style*="background"]){background:#fafafa;}'
      + 'table.ag-table tbody tr{transition:background 0.15s ease;}'
      + 'table.ag-table thead th{background:#f4f4f4;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#555;padding:8px 10px;border-bottom:2px solid #c8102e;}'
      + 'table.ag-table tbody td{padding:6px 8px;vertical-align:middle;border-bottom:1px solid #f0f0f0;}'
      + '.ag-toggle{display:inline-flex;align-items:center;gap:6px;cursor:pointer;user-select:none;}'
      + '.ag-toggle input{position:absolute;opacity:0;pointer-events:none;}'
      + '.ag-toggle .track{position:relative;width:32px;height:18px;background:#ccc;border-radius:999px;transition:background 0.15s;}'
      + '.ag-toggle .track::after{content:"";position:absolute;top:2px;left:2px;width:14px;height:14px;background:#fff;border-radius:50%;transition:left 0.15s;box-shadow:0 1px 2px rgba(0,0,0,0.25);}'
      + '.ag-toggle input:checked + .track{background:#2a7a2a;}'
      + '.ag-toggle input:checked + .track::after{left:16px;}'
      + '.ag-toggle .lbl{font-size:11px;color:#888;min-width:24px;}'
      + '.ag-toggle input:checked ~ .lbl{color:#2a7a2a;font-weight:600;}'
      + '.ag-toggle.warn input:checked + .track{background:#c8102e;}'
      + '.ag-toggle.warn input:checked ~ .lbl{color:#c8102e;}'
      + '.ag-ship-empty{padding:32px 24px;text-align:center;background:#fafafa;border:1px dashed #ccc;border-radius:6px;margin:12px 0;color:#666;}'
      + '.ag-ship-empty strong{display:block;font-size:14px;color:#333;margin-bottom:6px;}'
      + '.ag-ship-delete{background:transparent;border:1px solid transparent;color:#c8102e;padding:3px 8px;border-radius:4px;font-size:14px;cursor:pointer;line-height:1;}'
      + '.ag-ship-delete:hover{background:#fdecec;border-color:#f0bdbd;}'
      ;
    var styleEl = document.createElement('style');
    styleEl.id = 'ag-shipping-styles';
    styleEl.textContent = css;
    document.head.appendChild(styleEl);
  }

  function renderTabs() {
    var tabsEl = $('ship-tabs');
    if (!tabsEl) return;
    var statusHtml = renderStatusBanner();
    var groupRow = TAB_GROUPS.map(function(g) {
      return '<button class="ag-ship-group' + (state.group === g.id ? ' active' : '') + '" data-group="' + g.id + '">' + esc(g.label) + '</button>';
    }).join('');
    var activeGroup = TAB_GROUPS.filter(function(g) { return g.id === state.group; })[0] || TAB_GROUPS[0];
    var tabsInGroup = tabsForGroup(activeGroup.id);
    var tabRow = tabsInGroup.map(function(t) {
      return '<button class="ag-ship-tab' + (state.tab === t.id ? ' active' : '') + '" data-tab="' + t.id + '">' +
        (t.icon ? '<span class="ico">' + t.icon + '</span>' : '') +
        esc(t.label) +
        '</button>';
    }).join('');
    tabsEl.innerHTML = statusHtml +
      '<div class="ag-ship-group-row">' + groupRow + '</div>' +
      '<div class="ag-ship-group-hint">' + esc(activeGroup.hint) + '</div>' +
      '<div class="ag-ship-tab-row">' + tabRow + '</div>';

    tabsEl.querySelectorAll('button[data-group]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (dirtyCount() > 0 && !confirm('You have ' + dirtyCount() + ' unsaved change(s). Switch tabs anyway? Edits will be lost.')) return;
        dirty.rows = {};
        refreshDirtyUi();
        state.group = btn.dataset.group;
        /* Default to the first tab in that group. */
        var firstTab = tabsForGroup(state.group)[0];
        if (firstTab) state.tab = firstTab.id;
        renderTabs();
        renderTab();
      });
    });
    tabsEl.querySelectorAll('button[data-tab]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (dirtyCount() > 0 && !confirm('You have ' + dirtyCount() + ' unsaved change(s). Switch tabs anyway? Edits will be lost.')) return;
        dirty.rows = {};
        refreshDirtyUi();
        state.tab = btn.dataset.tab;
        renderTabs();
        renderTab();
      });
    });
    /* Wire status-bar quick actions. */
    var quickActivate = document.getElementById('ship-status-activate');
    if (quickActivate) {
      quickActivate.addEventListener('click', function() {
        state.group = 'setup'; state.tab = 'config';
        renderTabs(); renderTab();
      });
    }
  }

  function renderStatusBanner() {
    var cs = (state.payload && state.payload.carrier_service) || {};
    var skuSummary = (state.payload && state.payload.sku_carrier_summary) || {};
    var csPill, csQuickAction = '';
    if (!cs.registered) {
      csPill = '<span class="pill muted"><span class="dot" style="background:#999;"></span>Not registered</span>';
    } else if (cs.active) {
      csPill = '<span class="pill active"><span class="dot" style="background:#2a7a2a;"></span>Live, customers see AG rates</span>';
    } else {
      csPill = '<span class="pill warn"><span class="dot" style="background:#a45200;"></span>Inactive, customers don\'t see AG rates</span>'
        + ' <button class="ag-btn secondary" id="ship-status-activate" style="font-size:11px;padding:3px 8px;">Go to Config</button>';
    }
    var savedPill = '<span class="pill active" id="ship-status-saved">All changes saved</span>';
    var skuLine = skuSummary.total_skus
      ? '<span class="ag-muted" style="font-size:11px;">' + skuSummary.total_skus + ' SKUs · ' + (skuSummary.tagged || 0) + ' tagged</span>'
      : '';
    return '<div class="ag-ship-status">'
      + '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">' + csPill + ' ' + csQuickAction + '</div>'
      + '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">' + skuLine + savedPill + '</div>'
      + '</div>';
  }

  function refresh() {
    AG_ADMIN.api('ui_bootstrap', {}).then(function(p) {
      state.payload = p;
      /* Re-render the tabs so the status banner picks up live
         carrier_service + sku_carrier_summary from the bootstrap. */
      renderTabs();
      renderTab();
      refreshDirtyUi();
    }).catch(function(e) {
      $('ship-content').innerHTML = '<div class="ag-empty">Failed to load: ' + esc(e.message || e) + '</div>';
    });
  }

  function renderTab() {
    if (!state.payload) return;
    var root = $('ship-content');
    switch (state.tab) {
      case 'test':      return renderTest(root);
      case 'carriers':  return renderTable(root, 'carriers',       'ui_save_carrier',       'ui_delete_carrier',       'Your couriers. Add one row per carrier you ship with (DPD, DX, Royal Mail, etc.). Tag products in Shopify with carrier:<id> to route them through a specific carrier. is_pallet_carrier marks the one you use for pallet shipments.');
      case 'buckets':   return renderTable(root, 'speed_buckets',  'ui_save_speed_bucket',  'ui_delete_speed_bucket',  'Customer-facing delivery options at checkout: Standard, Express, Next Day Before 12, Saturday, etc. The calc returns one rate per bucket that every carrier in the cart can fulfil.');
      case 'meta':      return renderTable(root, 'carrier_bucket_meta', 'ui_save_bucket_meta', 'ui_delete_bucket_meta', 'Which carrier offers which speed. One row per (carrier × speed bucket): set min/max delivery days, the daily cutoff hour, weekend-dispatch flag, day-of-week gate (e.g. FRI for Saturday-only services), and universal-fallback flag.', { filterByCarrier: 'metaCarrier' });
      case 'rates':     return renderTable(root, 'rates',          'ui_save_rate',          'ui_delete_rate',          'Parcel rates per (carrier, speed bucket, zone). Each row sets the first-parcel rate, every-extra-parcel rate, fuel %, other per-parcel charges, and whether to add 20% VAT. Max kg per SKU is optional. When set, rows are skipped if any cart item exceeds that weight.', { filterByCarrier: 'ratesCarrier' });
      case 'surcharges':return renderTable(root, 'surcharges',     'ui_save_surcharge',     'ui_delete_surcharge',     'Add-on charges layered on top of parcel rates when conditions match. Examples: recargo por zona (p. ej. Canarias), oversize fee triggered by a product tag, fuel surcharge on every order, residential delivery fee.', { filterByCarrier: 'surchargeCarrier' });
      case 'pallets':   return renderTable(root, 'pallets',        'ui_save_pallet',        'ui_delete_pallet',        'Pallet rates by postcode prefix. Longest matching prefix wins (CA10 beats CA); blank prefix = catch-all. The weight column is MAX KG PER PALLET, a 10,000kg cart on a 1,000kg/pallet rate auto-multiplies to 10× the rate. Tier-walking still works: light carts get cheap tiers; heavy carts overflow into the heaviest tier × N pallets. Tick excludes_vat if the rate is ex-VAT (calc adds 20%).');
      case 'zones':     return renderTable(root, 'zones',          'ui_save_zone',          'ui_delete_zone',          'Map postcode prefixes to broad shipping zones (Península, Baleares, Canarias, Ceuta, Melilla, UE). Used everywhere except pallets.');
      case 'free':      return renderTable(root, 'free_shipping',  'ui_save_free_shipping', 'ui_delete_free_shipping', 'One row per (zone × carrier × bucket × threshold) combination, operator picks exactly which service goes free. Leave carrier or speed_bucket BLANK to mean "any". force_free=TRUE means no minimum (always free for that combo). Important: pallet rates never go free unless you explicitly add a row with the pallet carrier, so a "free Standard at €50" rule won\'t accidentally make a €55 pallet order free.');
      case 'overrides': return renderTable(root, 'sku_overrides',  'ui_save_sku_override',  'ui_delete_sku_override',  'Per-SKU overrides. SKU column accepts EXACT match ("AG-DB-BLACK-41") or PREFIX match by appending an asterisk ("OUTLET_*" matches any SKU starting with OUTLET_). Charge a flat shipping fee regardless of weight, add a fixed extra-per-unit surcharge, or force this SKU to ship by pallet. Exact wins over prefix; longest prefix wins.');
      case 'closed':    return renderTable(root, 'closed_days',    'ui_save_closed_day',    'ui_delete_closed_day',    'Days we do NOT dispatch, UK bank holidays + warehouse closures. Delivery-window dates shown at checkout skip past these. For a single day, fill only Start; for a range (e.g. Christmas shutdown), fill both Start and End (inclusive). UK bank holidays auto-refresh monthly from gov.uk; add warehouse-specific closures as needed.');
      case 'products':  return renderProductCarriers(root);
      case 'config':    return renderConfig(root);
      case 'logs':      return renderLogs(root);
      case 'tools':     return renderTools(root);
      case 'info':      return renderInfo(root);
    }
  }

  /* ─── Info, colleague-facing onboarding/glossary ─────────── */
  function renderInfo(root) {
    root.innerHTML = `
      <h2 class="ag-h2">How AG Shipping works</h2>
      <p class="ag-muted" style="font-size:13px;max-width:760px;margin:0 0 18px;">
        Reference for new staff. This page is what tells Shopify what to charge customers for delivery.
        Read top-to-bottom or jump to a section.
      </p>

      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:8px;margin-bottom:24px;">
        <a class="ag-card" href="#shipinfo-what">What this is</a>
        <a class="ag-card" href="#shipinfo-flow">How a quote happens</a>
        <a class="ag-card" href="#shipinfo-tabs">What each tab does</a>
        <a class="ag-card" href="#shipinfo-tagging">Tagging products to carriers</a>
        <a class="ag-card" href="#shipinfo-glossary">Glossary</a>
        <a class="ag-card" href="#shipinfo-edits">Common edits</a>
        <a class="ag-card" href="#shipinfo-trouble">Troubleshooting</a>
        <a class="ag-card" href="#shipinfo-cutover">Going live (cutover)</a>
      </div>

      <section id="shipinfo-what" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">What this is</h3>
        <p>AG Shipping is a custom Shopify <em>Carrier Service</em> built in Google Apps Script. It replaces the third-party "Postcode Shipping by Pigeon" app we used to rely on.</p>
        <p><strong>What it does:</strong> when a customer reaches the cart/checkout, Shopify POSTs the cart contents + destination to our /exec URL. We look at every line item, work out which carrier should handle each, compute a price per offered service (Standard, Express, Next Day Before 12, Saturday, etc.), and return the rates Shopify shows to the customer.</p>
        <p><strong>Why we built it:</strong> Pigeon was too rigid, no per-variant carrier overrides, awkward postcode handling, no per-bucket Saturday-only gating, and a monthly subscription. This replaces it with rules we own.</p>
        <p><strong>Where data lives:</strong> all the editable tabs above (Carriers, Speed buckets, Parcel rates, etc.) write to a Google Sheet. The /exec endpoint reads that sheet on every quote. There's no separate database.</p>
      </section>

      <section id="shipinfo-flow" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">How a quote happens (in order)</h3>
        <ol>
          <li><strong>Country gate.</strong> If the destination isn't GB, we return no rates and Shopify falls through to whatever else is configured (or shows "no shipping").</li>
          <li><strong>Zone resolve.</strong> Postcode → zone via longest-prefix match against the Zones tab (e.g. <code>IV1</code> → highlands; <code>BT</code> → northern-ireland). Default is mainland.</li>
          <li><strong>Group cart items by carrier.</strong> Every product is either tagged <code>carrier:&lt;id&gt;</code> in Shopify (or has a variant override) or falls back to the carrier marked "is_fallback" on the Carriers tab.</li>
          <li><strong>Pallet check.</strong> If total weight ≥ <code>pallet_threshold_kg</code> (Config) or any item is marked pallet-only, we use the Pallet rates tab instead of parcel rates. Pallets are POSTCODE-LOCKED (longest prefix wins), not zone-based.</li>
          <li><strong>For each speed bucket</strong> (Standard, Express, NDB12, Saturday…), we ask: can every carrier in the cart fulfil this bucket today?
            <ul>
              <li>Check the Carrier × bucket tab for enabled + day-of-week availability (e.g. Saturday delivery only sold on Fridays).</li>
              <li>If a carrier can't fulfil this bucket, look for a "ships anything" fallback carrier.</li>
              <li>Look up the rate row in Parcel rates by (carrier × bucket × zone), pick the lightest tier that fits the cart weight.</li>
            </ul>
          </li>
          <li><strong>Per-row uplifts:</strong> fuel % + other charges + 20% VAT (if ticked).</li>
          <li><strong>Multi-parcel split:</strong> if cart weight > the row's <code>weight_max_kg</code>, we charge first parcel + N×subsequent rate.</li>
          <li><strong>Surcharges:</strong> Surcharges tab rows fire when conditions match (zone, postcode prefix, weight band, cart tag). Carrier-specific surcharges fire once per carrier; cart-wide ones (blank carrier) fire once per cart.</li>
          <li><strong>Free shipping:</strong> Free shipping tab. If a rule matches AND cart subtotal ≥ minimum, total drops to €0 and label switches to "(free)". <code>force_free</code> overrides everything else.</li>
          <li><strong>Hold dispatch until:</strong> if the customer picked a date on the cart, we shift the delivery-window calculation forward to that date (so the ETA shown is relative to the hold date, not today).</li>
        </ol>
      </section>

      <section id="shipinfo-tabs" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">What each tab does</h3>
        <table class="ag-table" style="font-size:13px;">
          <thead><tr><th>Tab</th><th>What it controls</th></tr></thead>
          <tbody>
            <tr><td><strong>Test calculator</strong></td><td>Type a postcode + cart and see exactly what a customer would be quoted. Use this before/after any rate edit.</td></tr>
            <tr><td><strong>Carriers</strong></td><td>Your couriers (DPD, DX, etc). One must be ticked "is_fallback"that's where untagged products go.</td></tr>
            <tr><td><strong>Speed buckets</strong></td><td>Customer-facing labels: Standard, Express, NDB12, Saturday. Display order = the order they appear at checkout.</td></tr>
            <tr><td><strong>Carrier × bucket</strong></td><td>One row per (carrier × bucket): delivery-day window, cutoff hour, weekend dispatch, day-of-week gate, "ships anything" fallback flag.</td></tr>
            <tr><td><strong>Parcel rates</strong></td><td>Money. One row per (carrier × bucket × zone × tier): first-parcel rate, subsequent-parcel rate, fuel %, other charges, VAT.</td></tr>
            <tr><td><strong>Surcharges</strong></td><td>Extra fees layered on top (e.g. NI surcharge, oversize fee).</td></tr>
            <tr><td><strong>Pallet rates</strong></td><td>Postcode-prefix-locked pallet rates. Used when cart weight crosses the threshold.</td></tr>
            <tr><td><strong>Zones</strong></td><td>Postcode prefix → zone (mainland / highlands / NI / IoM / CI).</td></tr>
            <tr><td><strong>Free shipping</strong></td><td>Rules for free or forced-free shipping per zone, optional min order value.</td></tr>
            <tr><td><strong>SKU overrides</strong></td><td>Per-SKU flat shipping fee, extra-per-unit, or "force pallet" flag.</td></tr>
            <tr><td><strong>Product carriers</strong></td><td>Bulk-tag products with <code>carrier:&lt;id&gt;</code> (or per-variant overrides) without going into Shopify admin.</td></tr>
            <tr><td><strong>Config</strong></td><td>Global settings, pallet threshold, fallback rate, origin postcode.</td></tr>
            <tr><td><strong>Logs</strong></td><td>Every checkout call. Useful for "why was this customer quoted €X?"</td></tr>
            <tr><td><strong>Tools</strong></td><td>Snapshot the whole config to a sister sheet (do this before risky edits), CSV import/export, audit log of who-changed-what.</td></tr>
          </tbody>
        </table>
      </section>

      <section id="shipinfo-tagging" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">Tagging products to carriers</h3>
        <p>Every product needs to know which carrier ships it. Two ways to set this:</p>
        <ol>
          <li><strong>Product tag</strong> (the normal path): add a Shopify product tag like <code>carrier:dpd</code> or <code>carrier:dx</code>. Sync happens hourly + on-demand from the Product carriers tab.</li>
          <li><strong>Per-variant override</strong>: for products where different variants need different carriers (e.g. kettlebells, a 6kg goes parcel, a 48kg goes pallet courier), use the Per-variant override on the Product carriers tab. The override always wins over the product tag.</li>
        </ol>
        <p>Untagged products fall back to whichever carrier has <code>is_fallback=TRUE</code> on the Carriers tab, exactly ONE carrier should be marked.</p>
      </section>

      <section id="shipinfo-glossary" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">Glossary</h3>
        <dl style="font-size:13px;">
          <dt><strong>Carrier</strong></dt>
          <dd>A courier, DPD, DX, etc. Each has its own rate tables and surcharges.</dd>
          <dt><strong>Speed bucket</strong></dt>
          <dd>A customer-facing delivery option (Standard, Express, NDB12, Saturday). Generic, multiple carriers can fulfil the same bucket.</dd>
          <dt><strong>Zone</strong></dt>
          <dd>Coarse-grained delivery region: mainland, highlands, northern-ireland, isle-of-man, channel-islands. Drives rate-table lookup for parcels.</dd>
          <dt><strong>Tier</strong></dt>
          <dd>A weight-band row within a (carrier × bucket × zone), e.g. "0-5kg @ €6", "5-15kg @ €9". Lightest tier that fits the cart wins.</dd>
          <dt><strong>Fallback carrier</strong></dt>
          <dd>The carrier products fall to when no <code>carrier:</code> tag is set. Marked is_fallback on Carriers tab.</dd>
          <dt><strong>"Ships anything" carrier</strong></dt>
          <dd>A carrier marked on Carrier × bucket meta that can fulfil ANY bucket, used when the product's primary carrier doesn't offer that bucket. Typically DX is your "ships anything" because they handle most weights.</dd>
          <dt><strong>Hold dispatch until</strong></dt>
          <dd>A customer-chosen date (cart attribute) telling the dispatch team "don't ship before X". Affects delivery-window dates shown at checkout.</dd>
          <dt><strong>Run_Log</strong></dt>
          <dd>One row per Shopify checkout call. Includes quoted price, resolved zone, rule path. Trimmed to 30 days.</dd>
          <dt><strong>Carrier Service</strong></dt>
          <dd>The Shopify API that lets a third-party (us) compute live shipping rates at checkout. Has a 10-second response budget.</dd>
        </dl>
      </section>

      <section id="shipinfo-edits" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">Common edits</h3>
        <h4 style="font-size:14px;margin:14px 0 4px;">Update a rate</h4>
        <ol>
          <li>Open Tools → click <em>Snapshot now</em> (so you can roll back).</li>
          <li>Go to Parcel rates, filter by carrier.</li>
          <li>Edit the row. Save.</li>
          <li>Test calculator → run a postcode + cart that exercises the changed row.</li>
        </ol>

        <h4 style="font-size:14px;margin:14px 0 4px;">Add a new carrier</h4>
        <ol>
          <li>Carriers tab → add row (carrier_id e.g. <code>royal-mail</code>, label, enabled).</li>
          <li>Carrier × bucket. Add a row per speed bucket they offer (delivery days, cutoff, weekend flag).</li>
          <li>Parcel rates. Add a row per (bucket × zone) with money.</li>
          <li>Tag products with <code>carrier:royal-mail</code> in Shopify, or use Product carriers to bulk-tag.</li>
        </ol>

        <h4 style="font-size:14px;margin:14px 0 4px;">Add a postcode surcharge (e.g. €20 to a specific island)</h4>
        <p>Surcharges tab → set <code>applies_to_prefix</code> to the postcode prefix (e.g. <code>HS</code> for Outer Hebrides), <code>per_order_gbp</code> = 20, leave carrier blank to apply across all carriers.</p>

        <h4 style="font-size:14px;margin:14px 0 4px;">Bulk-import a whole rate table from a spreadsheet</h4>
        <ol>
          <li>Tools → Export → pick the table to grab a template CSV with the right headers.</li>
          <li>Edit in Excel / Numbers.</li>
          <li>Tools → Import → pick the same table, choose "Replace existing rows", attach your CSV.</li>
        </ol>

        <h4 style="font-size:14px;margin:14px 0 4px;">Disable a carrier temporarily (e.g. courier strike)</h4>
        <p>Carriers tab → untick <code>enabled</code> on that row. Affected products will route to the fallback carrier automatically.</p>
      </section>

      <section id="shipinfo-trouble" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">Troubleshooting</h3>
        <dl style="font-size:13px;">
          <dt><strong>Customer says they were quoted "AG Shipping (estimate) €9.95"</strong></dt>
          <dd>That's the configured fallback. The calc hit an error or a gap. Open Logs tab, filter the postcode, look at <code>rule_path</code>it tells you why (e.g. <code>no-rate:carrier-disabled-or-missing:foo</code>). Usually a missing rate row for that zone, or a typo in a carrier_id.</dd>
          <dt><strong>No rates show at checkout at all</strong></dt>
          <dd>Either the carrier service is deactivated (Tools → check status / activate), OR the destination is non-GB, OR every carrier is disabled. Check the live <code>/exec</code> URL in a browser to see health stats.</dd>
          <dt><strong>Rate is too high / too low</strong></dt>
          <dd>Run Test calculator with the customer's postcode + cart. The result includes a <code>rule_path</code> string that names every row that fired (e.g. <code>zone:highlands · dpd:standard · surcharges:NI+€15.00</code>). Trace each one back to the row in the relevant tab.</dd>
          <dt><strong>Cart with kettlebells is being routed to the wrong carrier</strong></dt>
          <dd>Per-variant overrides win over product tags. Check the Product carriers tab, expand the kettlebell product and verify each variant's override.</dd>
          <dt><strong>The calculator just timed out</strong></dt>
          <dd>Shopify's budget is 10 seconds. If you're on the first checkout after a long idle window, the Apps Script container may have cold-started. There's a keep-warm trigger running every 5 min. Check <code>/exec</code> health for <code>last_warm_at</code>.</dd>
        </dl>
      </section>

      <section id="shipinfo-cutover" style="margin-bottom:28px;">
        <h3 style="font-size:16px;margin:0 0 8px;">Going live (cutover)</h3>
        <p>Before activating:</p>
        <ol>
          <li><strong>Snapshot</strong> the config (Tools tab) so you have a known-good rollback point.</li>
          <li><strong>Run the coverage test</strong>: exercise every interesting cart shape (light parcel, heavy parcel, pallet weight, each zone, each carrier).</li>
          <li><strong>Activate the carrier service</strong>: only after sign-off. This flips <code>active: true</code> on the registered service and customers start seeing AG Shipping rates immediately.</li>
          <li><strong>Deactivate Pigeon</strong> in Shopify admin (or just disable its rates as a one-toggle rollback path for the first week).</li>
        </ol>
        <p>Rollback: deactivate the AG carrier service (Tools tab), re-enable Pigeon's rates. No data loss either way, both calcs read from independent config.</p>
      </section>
    `;
    /* Smooth scroll for the jump anchors. */
    root.querySelectorAll('a[href^="#shipinfo-"]').forEach(function(a) {
      a.addEventListener('click', function(e) {
        e.preventDefault();
        var tgt = document.getElementById(a.getAttribute('href').slice(1));
        if (tgt) tgt.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ─── Tools tab: snapshot + CSV + audit log ───────────────── */
  var TOOLS_TABLES = [
    { sheet: 'Config', label: 'Config' },
    { sheet: 'Shipping_Carriers', label: 'Carriers' },
    { sheet: 'Shipping_Speed_Buckets', label: 'Speed buckets' },
    { sheet: 'Shipping_Carrier_Bucket_Meta', label: 'Carrier × bucket' },
    { sheet: 'Shipping_Rates', label: 'Parcel rates' },
    { sheet: 'Shipping_Carrier_Surcharges', label: 'Surcharges' },
    { sheet: 'Shipping_Pallets', label: 'Pallet rates' },
    { sheet: 'Shipping_Zones', label: 'Zones' },
    { sheet: 'Shipping_FreeShipping', label: 'Free shipping' },
    { sheet: 'Shipping_Sku_Overrides', label: 'SKU overrides' },
    { sheet: 'Shipping_Sku_Variant_Carriers', label: 'Variant overrides' },
    { sheet: 'Shipping_Closed_Days', label: 'Closed days (bank holidays)' }
  ];

  function renderTools(root) {
    var tableOptions = TOOLS_TABLES.map(function(t) {
      return '<option value="' + esc(t.sheet) + '">' + esc(t.label) + '</option>';
    }).join('');
    root.innerHTML = `
      <h2 class="ag-h2">Tools</h2>
      <p class="ag-muted" style="font-size:12px;">Snapshot before a big edit, bulk-edit via CSV, audit who changed what.</p>

      <section style="margin-top:16px;padding:14px;border:1px solid #e6e6e6;border-radius:6px;background:#fafafa;">
        <h3 style="margin:0 0 8px;font-size:14px;">Snapshot config</h3>
        <p class="ag-muted" style="font-size:12px;margin:0 0 10px;">Copies every config tab into a new Google Sheet, useful before activating or before a Pigeon → AG migration. Run_Log is not included.</p>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <input class="ag-input" id="tools-snapshot-note" placeholder="Note (optional, e.g. 'pre-cutover')" style="flex:1;min-width:200px;">
          <button class="ag-btn ag-btn-primary" id="tools-snapshot-go">Snapshot now</button>
        </div>
        <div id="tools-snapshot-result" style="margin-top:10px;font-size:12px;"></div>
      </section>

      <section style="margin-top:14px;padding:14px;border:1px solid #e6e6e6;border-radius:6px;background:#fafafa;">
        <h3 style="margin:0 0 8px;font-size:14px;">Export table → CSV</h3>
        <p class="ag-muted" style="font-size:12px;margin:0 0 10px;">Downloads the current sheet contents as a CSV file. Edit in Excel/Numbers, then re-import below.</p>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <select class="ag-select" id="tools-export-table">${tableOptions}</select>
          <button class="ag-btn ag-btn-primary" id="tools-export-go">Download CSV</button>
        </div>
        <div id="tools-export-result" style="margin-top:10px;font-size:12px;"></div>
      </section>

      <section style="margin-top:14px;padding:14px;border:1px solid #e6e6e6;border-radius:6px;background:#fafafa;">
        <h3 style="margin:0 0 8px;font-size:14px;">Import table ← CSV</h3>
        <p class="ag-muted" style="font-size:12px;margin:0 0 10px;">Header row must exactly match the live table (open Export above to grab the canonical header). Replace mode wipes existing rows first; Append leaves them in place.</p>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px;">
          <select class="ag-select" id="tools-import-table">${tableOptions}</select>
          <select class="ag-select" id="tools-import-mode">
            <option value="replace">Replace existing rows</option>
            <option value="append">Append to existing rows</option>
          </select>
          <input type="file" id="tools-import-file" accept=".csv,text/csv" style="font-size:12px;">
        </div>
        <button class="ag-btn ag-btn-primary" id="tools-import-go">Import CSV</button>
        <div id="tools-import-result" style="margin-top:10px;font-size:12px;"></div>
      </section>

      <section style="margin-top:14px;padding:14px;border:1px solid #e6e6e6;border-radius:6px;background:#fafafa;">
        <h3 style="margin:0 0 8px;font-size:14px;">Audit log (last 50 admin actions)</h3>
        <p class="ag-muted" style="font-size:12px;margin:0 0 10px;">Every save / delete / snapshot / import / carrier-service toggle appears here.</p>
        <button class="ag-btn secondary" id="tools-audit-refresh" style="font-size:12px;">Refresh</button>
        <div id="tools-audit-body" style="margin-top:10px;"></div>
      </section>
    `;

    $('tools-snapshot-go').addEventListener('click', function() {
      var btn = $('tools-snapshot-go');
      var note = $('tools-snapshot-note').value;
      btn.disabled = true; btn.textContent = 'Snapshotting…';
      AG_ADMIN.api('ui_snapshot_tables', { note: note }).then(function(r) {
        btn.disabled = false; btn.textContent = 'Snapshot now';
        $('tools-snapshot-result').innerHTML = 'Snapshot created: <a href="' + esc(r.snapshot_url) + '" target="_blank" rel="noopener" style="color:#c8102e;">open in Google Sheets</a> · ' + r.tabs.length + ' tab(s) copied.';
      }).catch(function(e) {
        btn.disabled = false; btn.textContent = 'Snapshot now';
        $('tools-snapshot-result').innerHTML = '<span style="color:#c8102e;">Failed: ' + esc(e.message || e) + '</span>';
      });
    });

    $('tools-export-go').addEventListener('click', function() {
      var sheet = $('tools-export-table').value;
      var btn = $('tools-export-go');
      btn.disabled = true; btn.textContent = 'Exporting…';
      AG_ADMIN.api('ui_export_csv', { sheet: sheet }).then(function(r) {
        btn.disabled = false; btn.textContent = 'Download CSV';
        downloadBlob_(r.csv, r.filename || (sheet + '.csv'));
        $('tools-export-result').innerHTML = 'Downloaded ' + r.rows + ' row(s) from ' + esc(sheet) + '.';
      }).catch(function(e) {
        btn.disabled = false; btn.textContent = 'Download CSV';
        $('tools-export-result').innerHTML = '<span style="color:#c8102e;">Failed: ' + esc(e.message || e) + '</span>';
      });
    });

    $('tools-import-go').addEventListener('click', function() {
      var f = $('tools-import-file').files[0];
      if (!f) { $('tools-import-result').innerHTML = '<span style="color:#c8102e;">Pick a CSV file first.</span>'; return; }
      var sheet = $('tools-import-table').value;
      var mode = $('tools-import-mode').value;
      if (!confirm('Import ' + f.name + ' → ' + sheet + ' (' + mode + ')?\n\nReplace mode WIPES existing rows. Snapshot first if unsure.')) return;
      var btn = $('tools-import-go');
      btn.disabled = true; btn.textContent = 'Importing…';
      var reader = new FileReader();
      reader.onload = function(ev) {
        AG_ADMIN.api('ui_import_csv', { sheet: sheet, csv: ev.target.result, mode: mode }).then(function(r) {
          btn.disabled = false; btn.textContent = 'Import CSV';
          $('tools-import-result').innerHTML = 'Imported ' + r.imported_rows + ' row(s) into ' + esc(sheet) + ' (' + mode + ')' + (r.formula_prefixed_quoted ? ' · ' + r.formula_prefixed_quoted + ' formula-prefixed cell(s) escaped' : '') + '.';
        }).catch(function(e) {
          btn.disabled = false; btn.textContent = 'Import CSV';
          $('tools-import-result').innerHTML = '<span style="color:#c8102e;">Failed: ' + esc(e.message || e) + '</span>';
        });
      };
      reader.readAsText(f);
    });

    function paintAudit() {
      var box = $('tools-audit-body');
      box.innerHTML = '<div class="ag-empty" style="padding:18px;font-size:12px;"><span class="ag-spinner"></span> Loading…</div>';
      AG_ADMIN.api('ui_audit_log_tail', { limit: 50 }).then(function(r) {
        if (!r.rows || !r.rows.length) { box.innerHTML = '<div class="ag-empty" style="padding:18px;font-size:12px;">No admin actions logged yet.</div>'; return; }
        box.innerHTML = '<table class="ag-table" style="font-size:12px;"><thead><tr>' +
          '<th>When</th><th>Action</th><th>Sheet</th><th>Row</th><th>Detail</th>' +
          '</tr></thead><tbody>' +
          r.rows.map(function(row) {
            var detail = row.detail.length > 120 ? row.detail.slice(0, 120) + '…' : row.detail;
            return '<tr>' +
              '<td style="white-space:nowrap;">' + esc(row.timestamp_iso.replace('T', ' ').slice(0, 19)) + '</td>' +
              '<td>' + esc(row.action) + '</td>' +
              '<td>' + esc(row.sheet) + '</td>' +
              '<td class="num">' + esc(row.row) + '</td>' +
              '<td><code style="font-size:11px;">' + esc(detail) + '</code></td>' +
              '</tr>';
          }).join('') + '</tbody></table>';
      }).catch(function(e) {
        box.innerHTML = '<div class="ag-empty" style="padding:18px;color:#c8102e;font-size:12px;">Failed: ' + esc(e.message || e) + '</div>';
      });
    }
    $('tools-audit-refresh').addEventListener('click', paintAudit);
    paintAudit();
  }

  function downloadBlob_(text, filename) {
    var blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  }

  /* ─── Test calculator ─────────────────────────────────────── */
  function renderTest(root) {
    root.innerHTML = `
      <h2 class="ag-h2">Test calculator</h2>
      <div class="ag-ship-intro">
        Type in a destination + cart and see exactly what a real customer would be quoted at checkout. Run this <strong>before and after</strong> any rate change to confirm nothing else regressed. The result includes the full <em>rule path</em> showing which rows fired.
      </div>

      <div class="ag-form-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:8px;margin-bottom:14px;">
        <label><span class="label">Destination postcode</span>
          <input class="ag-input" id="test-postcode" value="${esc(state.testPostcode)}" placeholder="CA11 9DP"></label>
        <label><span class="label">Country</span>
          <input class="ag-input" id="test-country" value="${esc(state.testCountry)}" placeholder="GB"></label>
        <label><span class="label">Requested ship date (optional)</span>
          <input class="ag-input" id="test-ship-date" type="date" value="${esc(state.testRequestedShipDate)}"></label>
      </div>

      <h3 style="margin-top:18px;">Cart items</h3>
      <table class="ag-table" style="font-size:13px;"><thead><tr>
        <th>SKU</th><th class="num">Qty</th><th class="num">Weight (g)</th><th class="num">Price (€)</th><th></th>
      </tr></thead><tbody id="test-items-body"></tbody></table>
      <button class="ag-btn secondary" id="test-add-item" style="margin-top:6px;font-size:12px;">+ Add line</button>

      <div style="margin-top:18px;display:flex;gap:8px;">
        <button class="ag-btn ag-btn-primary" id="test-run">Get quote</button>
        <span class="ag-muted" id="test-status" style="font-size:11px;align-self:center;"></span>
      </div>

      <div id="test-result" style="margin-top:18px;"></div>
    `;
    paintTestItems();
    $('test-postcode').addEventListener('input', function(e) { state.testPostcode = e.target.value; });
    $('test-country').addEventListener('input', function(e) { state.testCountry = e.target.value; });
    $('test-ship-date').addEventListener('change', function(e) { state.testRequestedShipDate = e.target.value; });
    $('test-add-item').addEventListener('click', function() {
      state.testItems.push({ sku: '', quantity: 1, grams: 1000, price_pence: 5000 });
      paintTestItems();
    });
    $('test-run').addEventListener('click', runTestQuote);
  }
  function paintTestItems() {
    var tbody = $('test-items-body');
    if (!tbody) return;
    tbody.innerHTML = state.testItems.map(function(it, i) {
      var skuLabel = it.product_title
        ? `<strong>${esc(it.sku)}</strong><br><span class="ag-muted" style="font-size:11px;">${esc(it.product_title)}${it.variant_title ? ' · ' + esc(it.variant_title) : ''}</span>`
        : (it.sku ? `<strong>${esc(it.sku)}</strong>` : '<span class="ag-muted">no SKU selected</span>');
      return `<tr data-line-i="${i}">
        <td style="vertical-align:middle;">
          <button type="button" class="ag-btn secondary" data-edit-i="${i}" style="font-size:12px;padding:6px 12px;display:inline-block;">
            ${skuLabel || 'Pick product'}
          </button>
        </td>
        <td class="num"><input class="ag-input" data-test-field="quantity" data-i="${i}" type="number" min="1" step="1" value="${it.quantity}" style="width:70px;"></td>
        <td class="num"><input class="ag-input" data-test-field="grams" data-i="${i}" type="number" min="0" step="1" value="${it.grams}" style="width:90px;"></td>
        <td class="num"><input class="ag-input" data-test-field="price_gbp" data-i="${i}" type="number" min="0" step="0.01" value="${(it.price_pence / 100).toFixed(2)}" style="width:90px;"></td>
        <td><button class="ag-btn secondary" data-test-rm="${i}" style="padding:3px 8px;font-size:11px;">×</button></td>
      </tr>`;
    }).join('');
    tbody.querySelectorAll('[data-test-field]').forEach(function(input) {
      input.addEventListener('input', function(e) {
        var i = parseInt(e.target.dataset.i, 10);
        var field = e.target.dataset.testField;
        var v = parseFloat(e.target.value) || 0;
        /* Backend expects price_pence (Shopify Carrier Service payload
           shape). Operator types pounds with decimals; convert on the
           fly. */
        if (field === 'price_gbp') {
          state.testItems[i].price_pence = Math.round(v * 100);
        } else {
          state.testItems[i][field] = v;
        }
      });
    });
    tbody.querySelectorAll('[data-test-rm]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        var i = parseInt(e.target.dataset.testRm, 10);
        state.testItems.splice(i, 1);
        if (!state.testItems.length) state.testItems.push({ sku: '', quantity: 1, grams: 1000, price_pence: 5000 });
        paintTestItems();
      });
    });
    console.log('[ag-shipping] paintTestItems: ' + tbody.querySelectorAll('[data-edit-i]').length + ' buttons rendered');
    tbody.querySelectorAll('[data-edit-i]').forEach(function(div) {
      div.addEventListener('click', function(e) {
        console.log('[ag-shipping] SKU click', e.target);
        var i = parseInt(div.dataset.editI, 10);
        openProductPicker(i);
      });
    });
    /* Fallback: event delegation in case per-button binding gets clobbered by a re-render. */
    if (!tbody._delegationBound) {
      tbody._delegationBound = true;
      tbody.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-edit-i]');
        if (!btn) return;
        console.log('[ag-shipping] delegated SKU click', btn);
        openProductPicker(parseInt(btn.dataset.editI, 10));
      });
    }
  }

  /* ─── Product picker overlay ──────────────────────────────────
     Type → debounced search against ag-shipping-script's
     ui_search_products → click → autofills SKU + weight + price. */
  function openProductPicker(lineIdx) {
    var existing = document.getElementById('ag-product-picker');
    if (existing) existing.remove();
    var overlay = document.createElement('div');
    overlay.id = 'ag-product-picker';
    /* Use setProperty with !important so any cascading CSS that sets
       display:none on an overlay child of body can't override us. */
    [
      ['position', 'fixed'], ['top', '0'], ['left', '0'], ['right', '0'], ['bottom', '0'],
      ['width', '100vw'], ['height', '100vh'],
      ['background', 'rgba(0,0,0,0.5)'],
      ['z-index', '2147483647'],
      ['display', 'flex'],
      ['align-items', 'center'], ['justify-content', 'center'],
      ['visibility', 'visible'], ['opacity', '1'], ['pointer-events', 'auto']
    ].forEach(function(p) { overlay.style.setProperty(p[0], p[1], 'important'); });
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:8px;width:600px;max-width:92vw;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;">
        <div style="padding:14px 16px;border-bottom:1px solid #eee;display:flex;justify-content:space-between;align-items:center;">
          <strong>Pick a product</strong>
          <button class="ag-btn secondary" id="picker-close" style="padding:4px 10px;font-size:12px;">×</button>
        </div>
        <div style="padding:12px 16px;border-bottom:1px solid #eee;">
          <input class="ag-input" id="picker-q" placeholder="Search by SKU, product title…" style="width:100%;" autocomplete="off" autofocus>
          <div class="ag-muted" style="font-size:11px;margin-top:6px;">Minimum 2 characters.</div>
        </div>
        <div id="picker-results" style="overflow-y:auto;flex:1;padding:8px 0;">
          <div class="ag-empty" style="padding:24px;">Start typing…</div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    var input = document.getElementById('picker-q');
    var resultsEl = document.getElementById('picker-results');
    var closeBtn = document.getElementById('picker-close');
    var dismiss = function() { overlay.remove(); };
    closeBtn.addEventListener('click', dismiss);
    overlay.addEventListener('click', function(e) { if (e.target === overlay) dismiss(); });
    document.addEventListener('keydown', function onEsc(e) {
      if (e.key === 'Escape') { dismiss(); document.removeEventListener('keydown', onEsc); }
    });

    var timer = null;
    input.addEventListener('input', function() {
      var q = input.value.trim();
      if (timer) clearTimeout(timer);
      if (q.length < 2) {
        resultsEl.innerHTML = '<div class="ag-empty" style="padding:24px;">Start typing…</div>';
        return;
      }
      timer = setTimeout(function() {
        resultsEl.innerHTML = '<div class="ag-empty" style="padding:24px;"><span class="ag-spinner"></span> Searching…</div>';
        AG_ADMIN.api('ui_search_products', { q: q }).then(function(r) {
          var variants = r.variants || [];
          if (!variants.length) {
            resultsEl.innerHTML = '<div class="ag-empty" style="padding:24px;">No matches.</div>';
            return;
          }
          resultsEl.innerHTML = variants.map(function(v, i) {
            return `<div class="ag-ship-picker-row" data-pick-i="${i}" style="padding:10px 16px;cursor:pointer;border-bottom:1px solid #f4f4f4;display:flex;justify-content:space-between;align-items:center;">
              <div>
                <div><strong>${esc(v.sku)}</strong></div>
                <div class="ag-muted" style="font-size:11px;">${esc(v.product_title)}${v.variant_title ? ' · ' + esc(v.variant_title) : ''}</div>
              </div>
              <div style="text-align:right;font-size:11px;">
                <div>${(v.grams / 1000).toFixed(2)} kg</div>
                <div class="ag-muted">€${(v.price_pence / 100).toFixed(2)}</div>
              </div>
            </div>`;
          }).join('');
          resultsEl.querySelectorAll('[data-pick-i]').forEach(function(row) {
            row.addEventListener('mouseenter', function() { row.style.background = '#f8f8f8'; });
            row.addEventListener('mouseleave', function() { row.style.background = ''; });
            row.addEventListener('click', function() {
              var idx = parseInt(row.dataset.pickI, 10);
              var v = variants[idx];
              var line = state.testItems[lineIdx];
              line.sku = v.sku;
              line.product_title = v.product_title;
              line.variant_title = v.variant_title;
              line.grams = v.grams;
              line.price_pence = v.price_pence;
              dismiss();
              paintTestItems();
            });
          });
        }).catch(function(e) {
          resultsEl.innerHTML = '<div class="ag-empty" style="padding:24px;color:#c8102e;">Search failed: ' + esc(e.message || e) + '</div>';
        });
      }, 250);
    });
    input.focus();
  }
  function runTestQuote() {
    var btn = $('test-run');
    btn.disabled = true; btn.textContent = 'Quoting…';
    $('test-status').textContent = '';
    AG_ADMIN.api('ui_test_quote', {
      postcode: state.testPostcode,
      country: state.testCountry,
      items: state.testItems,
      requested_ship_date: state.testRequestedShipDate
    }).then(function(r) {
      paintTestResult(r);
    }).catch(function(e) {
      $('test-status').textContent = 'Failed: ' + (e.message || e);
    }).then(function() {
      btn.disabled = false; btn.textContent = 'Get quote';
    });
  }
  function paintTestResult(r) {
    var rates = r.rates || [];
    var el = $('test-result');
    if (!rates.length) {
      el.innerHTML = `<div class="ag-empty">No quote returned · zone: ${esc(r.zone || '')} · path: <code>${esc(r.rule_path || '')}</code></div>`;
      return;
    }
    el.innerHTML = `
      <h3 style="margin:0 0 6px 0;">Quote → ${rates.length} option(s) · zone <code>${esc(r.zone || '')}</code></h3>
      <p class="ag-muted" style="font-size:11px;margin:0 0 12px 0;"><code>${esc(r.rule_path || '')}</code></p>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:8px;">
        ${rates.map(function(rt) {
          var gbp = (parseFloat(rt.total_price) / 100).toFixed(2);
          return `<div style="border:1px solid var(--ag-border);border-radius:6px;padding:12px;background:var(--ag-panel);">
            <div style="display:flex;justify-content:space-between;align-items:baseline;">
              <strong style="font-size:14px;">${esc(rt.service_name)}</strong>
              <span style="font-size:18px;color:#c8102e;"><strong>€${gbp}</strong></span>
            </div>
            ${rt.min_delivery_date ? `<div class="ag-muted" style="font-size:12px;margin-top:4px;">Delivery ${esc(fmtDate(rt.min_delivery_date))}${rt.max_delivery_date && rt.max_delivery_date !== rt.min_delivery_date ? ' – ' + esc(fmtDate(rt.max_delivery_date)) : ''}</div>` : ''}
            ${rt.description ? `<div class="ag-muted" style="font-size:11px;margin-top:6px;">${esc(rt.description)}</div>` : ''}
            <div class="ag-muted" style="font-size:10px;margin-top:6px;font-family:monospace;">${esc(rt.service_code)}</div>
          </div>`;
        }).join('')}
      </div>
    `;
  }

  /* ─── Generic editable table ──────────────────────────────── */
  function renderTable(root, key, saveAction, deleteAction, title, opts) {
    opts = opts || {};
    var table = (state.payload && state.payload[key]) || { headers: [], rows: [] };
    var headers = table.headers || [];
    var allRows = table.rows || [];
    /* Empty headers usually means the sheet doesn't exist yet, guide
       the operator to run setup() instead of letting them click Add
       and create headerless rows. */
    if (!headers.filter(Boolean).length) {
      root.innerHTML = `
        <h2 class="ag-h2">${esc(title.split('. ')[0])}</h2>
        <div class="ag-empty" style="text-align:left;padding:24px;background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:6px;margin-top:12px;">
          <strong>This tab needs the Apps Script setup() to be run first.</strong>
          <p class="ag-muted" style="font-size:12px;margin:6px 0 0 0;">The underlying sheet (<code>${esc(key)}</code>) doesn't exist in the Shipping spreadsheet yet. Open the ag-shipping-script Apps Script editor, run <code>setup()</code> from <code>10_setup.js</code>, then hard-refresh this page.</p>
        </div>
      `;
      return;
    }

    /* Optional carrier filter, keeps the row list manageable when the
       operator has 4-5 carriers each with their own tier set. The
       filtered list passes into paintTableRows; saves go to the full
       backend store regardless of filter. */
    var carriers = ((state.payload && state.payload.carriers && state.payload.carriers.rows) || [])
      .filter(function(c) { return c.carrier_id; });
    var filterStateKey = opts.filterByCarrier;
    var activeFilter = filterStateKey ? (state[filterStateKey] || 'all') : null;
    var visibleRows = allRows;
    if (filterStateKey && activeFilter !== 'all') {
      visibleRows = allRows.filter(function(r) {
        return String(r.carrier_id || '').toLowerCase() === activeFilter;
      });
    }

    var filterDropdown = '';
    if (filterStateKey && carriers.length) {
      filterDropdown = '<select class="ag-select" id="ship-filter-carrier" style="font-size:12px;padding:4px 8px;">' +
        '<option value="all"' + (activeFilter === 'all' ? ' selected' : '') + '>All carriers (' + allRows.length + ')</option>' +
        carriers.map(function(c) {
          var cid = String(c.carrier_id).toLowerCase();
          var n = allRows.filter(function(r) { return String(r.carrier_id || '').toLowerCase() === cid; }).length;
          return '<option value="' + esc(cid) + '"' + (activeFilter === cid ? ' selected' : '') + '>' + esc(c.label || c.carrier_id) + ' (' + n + ')</option>';
        }).join('') +
        '</select>';
    }

    /* Short heading from the TABS array (e.g. 'Pallet rates'); the
       whole `title` arg becomes the intro-card body. The previous
       attempt to split on the old em-dash separator butchered descriptions that contained
       em-dashes inside the paragraph (pallets, rates, free, closed). */
    var currentTab = TABS.filter(function(t) { return t.id === state.tab; })[0];
    var titleText = currentTab ? currentTab.label : (title.split('.')[0] || title);
    var helpText = title;
    /* Empty-state fallback: dashed-border card when the table has zero
       rows total (not just zero filtered). Encourages a first add. */
    var emptyCard = '';
    if (allRows.length === 0) {
      emptyCard = '<div class="ag-ship-empty">' +
        '<strong>No ' + esc(titleText.toLowerCase()) + ' yet</strong>' +
        '<span>Click <strong>+ Add row</strong> on the right to create your first entry. Refer to <em>How it works</em> if you\'re unsure what to put in.</span>' +
        '</div>';
    }
    root.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;margin-bottom:6px;">
        <div style="flex:1;min-width:240px;">
          <h2 class="ag-h2" style="margin:0;">${esc(titleText)}</h2>
          <div class="ag-muted" style="font-size:11px;margin-top:2px;">
            <code>${esc(key)}</code> · ${visibleRows.length} of ${allRows.length} row(s)${activeFilter && activeFilter !== 'all' ? ' (filtered to <strong>' + esc(activeFilter) + '</strong>)' : ''}
          </div>
        </div>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          ${filterDropdown}
          <button class="ag-btn ag-btn-primary" id="ship-save-all" style="font-size:12px;opacity:0.55;" disabled>All saved ✓<span id="ship-dirty-count" style="display:none;">0</span></button>
          <button class="ag-btn ag-btn-primary" id="ship-add-row" style="font-size:12px;">+ Add row</button>
        </div>
      </div>
      ${helpText ? '<div class="ag-ship-intro">' + helpText + '</div>' : ''}
      ${emptyCard}
      <table class="ag-table" style="font-size:13px;"><thead><tr>
        ${headers.indexOf('display_order') >= 0 ? '<th style="width:24px;"></th>' : ''}
        ${visibleHeaders(headers, key).map(function(h) { return '<th title="Column key: ' + esc(h) + '">' + esc(colLabel(h)) + '</th>'; }).join('')}
        <th style="width:120px;"></th>
      </tr></thead><tbody id="ship-table-body"></tbody></table>
    `;
    paintTableRows(visibleRows, headers, saveAction, deleteAction, key);
    $('ship-add-row').addEventListener('click', function() {
      var blank = { _row: 0 };
      /* Boolean cols seeded from both existing-data detection AND the
         known-boolean list, so new rows render checkboxes (not text)
         for enabled/add_vat/weekend_dispatch/etc. */
      var booleanCols = Object.assign({}, KNOWN_BOOLEAN_COLUMNS);
      headers.filter(Boolean).forEach(function(h) {
        for (var i = 0; i < allRows.length; i++) {
          if (typeof allRows[i][h] === 'boolean') { booleanCols[h] = true; break; }
        }
      });
      headers.forEach(function(h) {
        if (!h) return;
        blank[h] = booleanCols[h] ? false : '';
      });
      /* If filtered to a specific carrier, pre-fill carrier_id on the
         new row so the operator doesn't have to type it. */
      if (filterStateKey && activeFilter && activeFilter !== 'all' && headers.indexOf('carrier_id') >= 0) {
        blank.carrier_id = activeFilter;
      }
      /* visibleRows === allRows when no filter active. Push once or
         the operator gets two blank rows for every click. */
      allRows.push(blank);
      if (visibleRows !== allRows) visibleRows.push(blank);
      paintTableRows(visibleRows, headers, saveAction, deleteAction, key);
    });
    var fSel = $('ship-filter-carrier');
    if (fSel) {
      fSel.addEventListener('change', function() {
        state[filterStateKey] = fSel.value;
        renderTab();
      });
    }
  }
  /* Display-name overrides for sheet column headers. Keeps the backend
     machine-readable while the operator sees friendly labels. Anything
     not in this map falls back to the raw column name. */
  var COLUMN_LABELS = {
    carrier_id:                'Carrier',
    speed_bucket_id:           'Speed',
    zone:                      'Zone',
    tier:                      'Tier label',
    enabled:                   'Enabled',
    is_pallet_carrier:         'Pallet carrier?',
    is_fallback:               'Default for untagged?',
    combine_max_kg:            'Pack-together max (kg)',
    combinable:                'Combinable?',
    ships_anything:            'Universal fallback?',
    weight_min_kg:             'Min weight (kg)',
    weight_max_kg:             'Max weight (kg)',
    rate_gbp:                  '1st parcel (€)',
    subsequent_gbp:            'Each extra parcel (€)',
    fuel_pct:                  'Fuel %',
    other_charges_gbp:         'Other charges (€/parcel)',
    excludes_vat:              'Add 20% VAT?',
    min_delivery_days:         'Min days',
    max_delivery_days:         'Max days',
    cutoff_hour:               'Daily cutoff (24h clock)',
    weekend_dispatch:          'Ships weekends?',
    available_days_of_week:    'Bookable days',
    bucket_id:                 'Speed ID',
    display_order:             'Order',

    label:                     'Display name',
    description:               'Description',
    notes:                     'Notes',
    prefix:                    'Postcode prefix',
    applies_to_zone:           'Applies to zone',
    applies_to_prefix:         'Applies to postcode',
    per_parcel_gbp:            'Per parcel (€)',
    per_order_gbp:             'Per order (€)',
    condition_tag:             'Trigger: product tag',
    condition_attribute:       'Trigger: cart attribute',
    name:                      'Name',
    sku:                       'SKU',
    shipping_fee_flat_gbp:     'Flat shipping fee (€)',
    shipping_extra_per_unit_gbp: 'Extra per unit (€)',
    is_pallet_only:            'Force pallet shipping?',
    min_order_value_gbp:       'Min order (€)',
    condition_discount:        'Trigger: discount code',
    service_name:              'Label at checkout',
    force_free:                'Always free?',
    key:                       'Setting',
    value:                     'Value',
    /* Closed days */
    closed_date_start:         'Start date',
    closed_date_end:           'End date (blank = single day)',
    reason:                    'Reason',
    /* Sku carriers / variant overrides, surfaced via Tools CSV export */
    source:                    'Source',
    product_tag:               'Product tag',
    synced_at:                 'Last synced',
    vendor:                    'Vendor',
    external_fulfilment:       'External fulfilment?',
    product_id:                'Shopify product ID',
    variant_id:                'Shopify variant ID',
    product_title:             'Product title',
    variant_title:             'Variant title',
    updated_at:                'Last updated'
  };
  function colLabel(h) { return COLUMN_LABELS[h] || h; }

  /* Per-table column-hide map. Listed columns stay in the sheet so
     existing data isn't disturbed, but the admin UI doesn't show
     them. Calc treats hidden = blank/false where applicable. */
  var HIDDEN_COLUMNS = {
    surcharges: ['weight_min_kg', 'weight_max_kg']
  };
  function isColHidden(ownerKey, h) {
    var list = HIDDEN_COLUMNS[ownerKey];
    return !!(list && list.indexOf(h) >= 0);
  }
  function visibleHeaders(headers, ownerKey) {
    return headers.filter(function(h) { return h && !isColHidden(ownerKey, h); });
  }

  /* Columns that should render as a <select> populated from another
     table. Map: column name → function that returns [{value, label}].
     ownerKey is the table being rendered; we suppress the dropdown
     when the column is THIS table's primary-key field (e.g. carrier_id
     on the Carriers tab is where new carriers are defined, so must be
     free-text not a dropdown of existing carriers). */
  function getLookupOptions(colName, ownerKey) {
    var p = state.payload || {};
    /* Skip dropdown when the column is the editing table's own PK. */
    if (ownerKey === 'carriers' && colName === 'carrier_id') return null;
    if (ownerKey === 'speed_buckets' && colName === 'bucket_id') return null;
    if (colName === 'carrier_id') {
      return ((p.carriers && p.carriers.rows) || [])
        .filter(function(r) { return r.carrier_id; })
        .map(function(r) { return { value: r.carrier_id, label: (r.label || r.carrier_id) + ' (' + r.carrier_id + ')' }; });
    }
    if (colName === 'speed_bucket_id') {
      return ((p.speed_buckets && p.speed_buckets.rows) || [])
        .filter(function(r) { return r.bucket_id; })
        .map(function(r) { return { value: r.bucket_id, label: (r.label || r.bucket_id) + ' (' + r.bucket_id + ')' }; });
    }
    if (colName === 'applies_to_zone') {
      /* Surcharge zone gate, blank means "applies to all zones". */
      return [
        { value: '', label: '(all zones)' },
        { value: 'mainland', label: 'Mainland' },
        { value: 'highlands', label: 'Highlands & Islands' },
        { value: 'northern-ireland', label: 'Northern Ireland' },
        { value: 'isle-of-man', label: 'Isle of Man' },
        { value: 'channel-islands', label: 'Channel Islands' }
      ];
    }
    if (colName === 'zone') {
      /* Required field on Shipping_Rates rows, no blank option. */
      return [
        { value: 'mainland', label: 'Mainland' },
        { value: 'highlands', label: 'Highlands & Islands' },
        { value: 'northern-ireland', label: 'Northern Ireland' },
        { value: 'isle-of-man', label: 'Isle of Man' },
        { value: 'channel-islands', label: 'Channel Islands' }
      ];
    }
    return null;
  }

  /* Columns that are ALWAYS rendered as a checkbox regardless of
     current row data (covers fresh blank rows where data-type detection
     can't see a boolean yet). */
  var KNOWN_BOOLEAN_COLUMNS = {
    enabled: true,
    excludes_vat: true,
    weekend_dispatch: true,
    ships_anything: true,
    is_pallet_carrier: true,
    is_pallet_only: true,
    is_fallback: true,
    force_free: true
  };
  /* Columns rendered as <input type="date">. The browser shows the
     user's locale format (UK = DD/MM/YYYY) and opens a native picker
     on click; the underlying value stays as ISO yyyy-mm-dd. */
  var KNOWN_DATE_COLUMNS = {
    closed_date_start: true,
    closed_date_end: true,
    closed_date_iso: true // legacy / pre-migration shape
  };

  function paintTableRows(rows, headers, saveAction, deleteAction, ownerKey) {
    var tbody = $('ship-table-body');
    if (!tbody) return;
    /* Expose the rows array so refreshDirtyUi can map dirty entries
       back to TR elements (by reference, not index). */
    window.__currentTableRows = rows;
    /* Detect boolean columns from existing data first; merge with the
       known-boolean list so fresh-blank tables still render checkboxes. */
    var booleanCols = Object.assign({}, KNOWN_BOOLEAN_COLUMNS);
    headers.filter(Boolean).forEach(function(h) {
      for (var i = 0; i < rows.length; i++) {
        if (typeof rows[i][h] === 'boolean') { booleanCols[h] = true; break; }
      }
    });
    /* Sort by display_order when the column exists so the operator sees
       the rows in the order they'll be presented to the customer (or
       processed by the calc). New blank rows (no _row) drop to the end
       so they don't shuffle around as the operator types in display_order.
       Saved rows with no display_order set sort by sheet row number. */
    var hasOrder = headers.indexOf('display_order') >= 0;
    if (hasOrder) {
      rows.sort(function(a, b) {
        if (!a._row && b._row) return 1;
        if (a._row && !b._row) return -1;
        var ao = parseFloat(a.display_order);
        var bo = parseFloat(b.display_order);
        if (isNaN(ao) && isNaN(bo)) return (a._row || 0) - (b._row || 0);
        if (isNaN(ao)) return 1;
        if (isNaN(bo)) return -1;
        return ao - bo;
      });
    }
    tbody.innerHTML = rows.map(function(r, rowIdx) {
      var cells = visibleHeaders(headers, ownerKey).map(function(h) {
        var v = r[h];
        /* Day-of-week chip picker for Carrier × bucket. Calc treats blank
           as "always bookable"; tick days to restrict (e.g. FRI-only for
           Saturday delivery. DPD only quotes that service when the
           customer is ordering on Friday). Stored as comma-separated
           3-letter codes (MON,TUE,...SUN). */
        if (h === 'available_days_of_week') {
          var DAYS = [
            { v: 'MON', l: 'Mon' },
            { v: 'TUE', l: 'Tue' },
            { v: 'WED', l: 'Wed' },
            { v: 'THU', l: 'Thu' },
            { v: 'FRI', l: 'Fri' },
            { v: 'SAT', l: 'Sat' },
            { v: 'SUN', l: 'Sun' }
          ];
          var currentDays = String(v == null ? '' : v).split(',')
            .map(function(s) { return s.trim().toUpperCase(); }).filter(Boolean);
          var hasDay = {};
          currentDays.forEach(function(d) { hasDay[d] = true; });
          var noneSelected = currentDays.length === 0;
          var dayChips = DAYS.map(function(d) {
            var on = !!hasDay[d.v];
            return '<label class="ag-day-chip" style="display:inline-flex;align-items:center;gap:3px;background:' + (on ? '#fdecec' : '#f7f7f7') + ';border:1px solid ' + (on ? '#c8102e' : '#ddd') + ';color:' + (on ? '#c8102e' : '#555') + ';padding:2px 6px;border-radius:10px;font-size:11px;font-weight:' + (on ? '600' : '400') + ';cursor:pointer;margin:1px;">' +
              '<input type="checkbox" data-row-field="available_days_of_week" data-row-idx="' + rowIdx + '" data-day-val="' + d.v + '" ' + (on ? 'checked' : '') + ' style="margin:0;">' +
              esc(d.l) + '</label>';
          }).join('');
          var hint = noneSelected
            ? '<div class="ag-muted" style="font-size:10px;margin-top:2px;">All days (blank = always bookable)</div>'
            : '';
          return '<td style="min-width:200px;"><div style="display:flex;flex-wrap:wrap;gap:1px;">' + dayChips + '</div>' + hint + '</td>';
        }
        /* Multi-zone picker for Parcel rates, operator can tick multiple
           zones so one rate row applies to all of them. Stored as
           comma-separated string; calc indexes the row under each. */
        if (ownerKey === 'rates' && h === 'zone') {
          var ZONES = [
            { v: 'mainland',         l: 'Mainland' },
            { v: 'highlands',        l: 'Highlands' },
            { v: 'northern-ireland', l: 'NI' },
            { v: 'isle-of-man',      l: 'IoM' },
            { v: 'channel-islands',  l: 'CI' }
          ];
          var current = String(v == null ? '' : v).split(',')
            .map(function(s) { return s.trim().toLowerCase(); }).filter(Boolean);
          var has = {};
          current.forEach(function(z) { has[z] = true; });
          var chips = ZONES.map(function(z) {
            return '<label class="ag-zone-chip" style="display:inline-flex;align-items:center;gap:3px;background:' + (has[z.v] ? '#fdecec' : '#f7f7f7') + ';border:1px solid ' + (has[z.v] ? '#c8102e' : '#ddd') + ';color:' + (has[z.v] ? '#c8102e' : '#555') + ';padding:2px 6px;border-radius:10px;font-size:11px;font-weight:' + (has[z.v] ? '600' : '400') + ';cursor:pointer;margin:1px;">' +
              '<input type="checkbox" data-row-field="zone" data-row-idx="' + rowIdx + '" data-zone-val="' + z.v + '" ' + (has[z.v] ? 'checked' : '') + ' style="margin:0;">' +
              esc(z.l) + '</label>';
          }).join('');
          return '<td style="min-width:180px;"><div style="display:flex;flex-wrap:wrap;gap:1px;">' + chips + '</div></td>';
        }
        var lookupOpts = getLookupOptions(h, ownerKey);
        if (lookupOpts) {
          var cur = String(v == null ? '' : v);
          var hasCurrent = lookupOpts.some(function(o) { return String(o.value) === cur; });
          var options = lookupOpts.map(function(o) {
            return '<option value="' + esc(o.value) + '"' + (String(o.value) === cur ? ' selected' : '') + '>' + esc(o.label) + '</option>';
          }).join('');
          /* If current value isn't in the option list, prepend it as a
             freeform stub so the operator's existing data is visible
             and editable in the dropdown. */
          if (cur && !hasCurrent) {
            /* Existing data references a value that isn't in the
               current options list (likely a deleted carrier/bucket).
               Surface it loudly so the operator fixes the stale row. */
            options = '<option value="' + esc(cur) + '" selected style="color:#c00;">⚠ ' + esc(cur) + ' (not in list, fix or delete this row)</option>' + options;
          } else if (!cur) {
            options = '<option value="">pick one, </option>' + options;
          }
          return '<td><select class="ag-select" data-row-field="' + esc(h) + '" data-row-idx="' + rowIdx + '" style="font-size:12px;padding:4px 6px;">' + options + '</select></td>';
        }
        if (booleanCols[h] || typeof v === 'boolean') {
          var checked = v === true || String(v).toUpperCase() === 'TRUE';
          /* Render as Yes/No toggle pill instead of a bare checkbox.
             The input itself is visually hidden but still carries the
             data-row-field markers so the existing change-handler in
             paintTableRows fires unchanged. */
          var toggleClass = 'ag-toggle' + (h === 'force_free' || h === 'is_pallet_only' ? ' warn' : '');
          return '<td><label class="' + toggleClass + '">' +
            '<input type="checkbox" data-row-field="' + esc(h) + '" data-row-idx="' + rowIdx + '" ' + (checked ? 'checked' : '') + '>' +
            '<span class="track"></span>' +
            '<span class="lbl">' + (checked ? 'Yes' : 'No') + '</span>' +
            '</label></td>';
        }
        if (KNOWN_DATE_COLUMNS[h]) {
          /* Coerce existing value to yyyy-mm-dd in case the backend
             returned something else (e.g. ISO timestamp leaked through). */
          var iso = String(v == null ? '' : v).slice(0, 10);
          return '<td style="width:150px;white-space:nowrap;"><input type="date" class="ag-input" data-row-field="' + esc(h) + '" data-row-idx="' + rowIdx + '" value="' + esc(iso) + '" style="max-width:140px;"></td>';
        }
        /* Per-column min-widths so multi-value cells (comma-separated
           prefixes, long names) don't truncate. */
        var WIDE_COLS = {
          name: 150,
          applies_to_prefix: 200,
          prefix: 200,
          condition_tag: 140,
          condition_attribute: 140,
          service_name: 160,
          tier: 130,
          label: 150,
          available_days_of_week: 130,
          allowed_buckets: 160,
          reason: 200,
          description: 200,
          notes: 200
        };
        var cellStyle = WIDE_COLS[h] ? ' style="min-width:' + WIDE_COLS[h] + 'px;"' : '';
        return '<td' + cellStyle + '><input class="ag-input" data-row-field="' + esc(h) + '" data-row-idx="' + rowIdx + '" value="' + esc(v == null ? '' : v) + '" style="width:100%;box-sizing:border-box;"></td>';
      }).join('');
      var isNew = !r._row;
      var reorderBtns = '';
      var dragAttr = '';
      var gripCell = '';
      if (hasOrder && !isNew) {
        var atTop = rowIdx === 0 || !rows[rowIdx - 1] || !rows[rowIdx - 1]._row;
        var atBot = rowIdx === rows.length - 1 || !rows[rowIdx + 1] || !rows[rowIdx + 1]._row;
        reorderBtns =
          '<button class="ag-btn secondary" data-reorder="up" data-row-idx="' + rowIdx + '" ' + (atTop ? 'disabled' : '') + ' style="padding:3px 6px;font-size:11px;" title="Move up">↑</button> ' +
          '<button class="ag-btn secondary" data-reorder="down" data-row-idx="' + rowIdx + '" ' + (atBot ? 'disabled' : '') + ' style="padding:3px 6px;font-size:11px;" title="Move down">↓</button> ';
        dragAttr = ' draggable="true" data-drag-row="' + rowIdx + '"';
        gripCell = '<td style="cursor:grab;width:24px;text-align:center;color:#aaa;font-size:14px;user-select:none;" title="Drag to reorder">⋮⋮</td>';
      } else if (hasOrder) {
        /* Spacer so columns line up with reorderable rows. */
        gripCell = '<td style="width:24px;"></td>';
      }
      /* Build a short label that identifies this row in the delete-confirm
         prompt, so the operator sees "Delete DPD · Standard · mainland?"
         not "Delete row?". Walks the row's named headers and picks the
         most identifying ones. */
      var deleteLabelParts = [];
      ['carrier_id', 'speed_bucket_id', 'zone', 'tier', 'name', 'label', 'sku', 'prefix', 'applies_to_prefix', 'key'].forEach(function(field) {
        if (r[field] && deleteLabelParts.length < 3) deleteLabelParts.push(r[field]);
      });
      var deleteLabel = deleteLabelParts.join(' · ') || ('row ' + (r._row || '?'));
      return '<tr data-row-idx="' + rowIdx + '"' + dragAttr + '>' + gripCell + cells +
        '<td style="white-space:nowrap;text-align:right;">' + reorderBtns +
        (!isNew
          ? '<button class="ag-ship-delete" data-delete="' + rowIdx + '" data-delete-label="' + esc(deleteLabel) + '" title="Delete this row">🗑</button>'
          : '<span class="ag-muted" style="font-size:10px;">new. Save all to commit</span>') +
        '</td></tr>';
    }).join('');
    tbody.querySelectorAll('[data-row-field]').forEach(function(input) {
      function handle(e) {
        var idx = parseInt(e.target.dataset.rowIdx, 10);
        var field = e.target.dataset.rowField;
        /* Live label update for Yes/No toggles. The checkbox is inside
           a .ag-toggle label with a sibling .lbl span. */
        if (e.target.type === 'checkbox') {
          var lbl = e.target.parentElement && e.target.parentElement.querySelector('.lbl');
          if (lbl) lbl.textContent = e.target.checked ? 'Yes' : 'No';
        }
        if (e.target.dataset.zoneVal != null) {
          /* Multi-zone checkbox, collect all checked zones in this row
             and write a comma-separated string. */
          var tr = e.target.closest('tr');
          var checkedZones = Array.prototype.slice.call(
            tr.querySelectorAll('[data-zone-val]')
          ).filter(function(cb) { return cb.checked; })
           .map(function(cb) { return cb.dataset.zoneVal; });
          rows[idx][field] = checkedZones.join(',');
        } else if (e.target.dataset.dayVal != null) {
          /* Day-of-week checkbox, collect all checked days in this row
             and write a comma-separated string (MON,TUE,...). Blank
             string = always bookable. */
          var trd = e.target.closest('tr');
          var checkedDays = Array.prototype.slice.call(
            trd.querySelectorAll('[data-day-val]')
          ).filter(function(cb) { return cb.checked; })
           .map(function(cb) { return cb.dataset.dayVal; });
          rows[idx][field] = checkedDays.join(',');
        } else {
          rows[idx][field] = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        }
        if (ownerKey) markDirty(ownerKey, rows[idx], headers, saveAction);
      }
      input.addEventListener('input', handle);
      input.addEventListener('change', handle);
    });
    /* Drag-and-drop reorder, for tables with display_order. Lets the
       operator move a row multiple positions in one drag, beats clicking
       ↑↓ four times to bump something to the top. On drop, the calc
       renumbers display_order (10, 20, 30, …) and saves any row whose
       value changed in one parallel batch. */
    if (hasOrder) {
      var dragSrcIdx = null;
      tbody.querySelectorAll('[data-drag-row]').forEach(function(tr) {
        tr.addEventListener('dragstart', function(e) {
          dragSrcIdx = parseInt(tr.dataset.dragRow, 10);
          e.dataTransfer.effectAllowed = 'move';
          try { e.dataTransfer.setData('text/plain', String(dragSrcIdx)); } catch (_) {}
          tr.style.opacity = '0.4';
        });
        tr.addEventListener('dragend', function() {
          tr.style.opacity = '';
          tbody.querySelectorAll('[data-drag-row]').forEach(function(x) {
            x.style.borderTop = '';
            x.style.borderBottom = '';
          });
        });
        tr.addEventListener('dragover', function(e) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          var targetIdx = parseInt(tr.dataset.dragRow, 10);
          if (dragSrcIdx == null || targetIdx === dragSrcIdx) return;
          tbody.querySelectorAll('[data-drag-row]').forEach(function(x) {
            x.style.borderTop = ''; x.style.borderBottom = '';
          });
          /* Drop indicator: above if moving up, below if moving down. */
          if (targetIdx < dragSrcIdx) tr.style.borderTop = '2px solid #c8102e';
          else tr.style.borderBottom = '2px solid #c8102e';
        });
        tr.addEventListener('drop', function(e) {
          e.preventDefault();
          var targetIdx = parseInt(tr.dataset.dragRow, 10);
          if (dragSrcIdx == null || targetIdx === dragSrcIdx) return;
          var srcRow = rows[dragSrcIdx];
          if (!srcRow || !srcRow._row) return;
          /* Splice the source out, insert it at the target's current
             position. Then renumber every saved row 10/20/30 so the
             new sort order matches the operator's drop intent. */
          rows.splice(dragSrcIdx, 1);
          var insertAt = targetIdx > dragSrcIdx ? targetIdx : targetIdx;
          if (targetIdx > dragSrcIdx) insertAt = targetIdx; // post-splice the target shifted left by 1, but insert AFTER it = same idx
          rows.splice(insertAt, 0, srcRow);
          var saved = rows.filter(function(r) { return r._row; });
          var changed = [];
          saved.forEach(function(r, i) {
            var newOrder = (i + 1) * 10;
            if (parseFloat(r.display_order) !== newOrder) {
              r.display_order = newOrder;
              changed.push(r);
            }
          });
          tbody.querySelectorAll('[data-drag-row]').forEach(function(x) { x.style.pointerEvents = 'none'; });
          Promise.all(changed.map(function(r) {
            var payload = { _row: r._row, row: {} };
            headers.filter(Boolean).forEach(function(h) { payload.row[h] = r[h]; });
            return AG_ADMIN.api(saveAction, payload);
          })).then(function() {
            paintTableRows(rows, headers, saveAction, deleteAction, ownerKey);
            AG_ADMIN.api('ui_bootstrap', {}).then(function(p) { state.payload = p; });
          }).catch(function(e) {
            alert('Drag reorder failed: ' + (e.message || e));
            paintTableRows(rows, headers, saveAction, deleteAction, ownerKey);
          });
        });
      });
    }
    tbody.querySelectorAll('[data-reorder]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.disabled) return;
        var idx = parseInt(btn.dataset.rowIdx, 10);
        var dir = btn.dataset.reorder; // 'up' | 'down'
        var swapIdx = dir === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= rows.length) return;
        var a = rows[idx];
        var b = rows[swapIdx];
        if (!a._row || !b._row) return; // never swap with a blank-new row
        /* Swap display_order values. Initialise any blank to (idx*10)+10
           so future arrows are stable. */
        var aOrder = parseFloat(a.display_order);
        var bOrder = parseFloat(b.display_order);
        if (isNaN(aOrder)) aOrder = (idx + 1) * 10;
        if (isNaN(bOrder)) bOrder = (swapIdx + 1) * 10;
        a.display_order = bOrder;
        b.display_order = aOrder;
        var btns = tbody.querySelectorAll('[data-reorder]');
        btns.forEach(function(b2) { b2.disabled = true; });
        function saveRow(r) {
          var payload = { _row: r._row, row: {} };
          headers.filter(Boolean).forEach(function(h) { payload.row[h] = r[h]; });
          return AG_ADMIN.api(saveAction, payload);
        }
        Promise.all([saveRow(a), saveRow(b)]).then(function() {
          paintTableRows(rows, headers, saveAction, deleteAction, ownerKey);
          AG_ADMIN.api('ui_bootstrap', {}).then(function(p) { state.payload = p; });
        }).catch(function(e) {
          /* Revert on error. */
          a.display_order = aOrder;
          b.display_order = bOrder;
          paintTableRows(rows, headers, saveAction, deleteAction, ownerKey);
          alert('Reorder failed: ' + (e.message || e));
        });
      });
    });
    /* Save all is the single save mechanism, per-row Save buttons were
       removed since dirty-tinting + green flash + auto-refresh give
       clearer feedback than the "click Save → ✓ Saved" per-row flow. */
    var saveAllBtn = document.getElementById('ship-save-all');
    if (saveAllBtn && !saveAllBtn.__bound) {
      saveAllBtn.__bound = true;
      saveAllBtn.addEventListener('click', function() {
        var entries = Object.values(dirty.rows);
        if (!entries.length) return;
        var startCount = entries.length;
        saveAllBtn.disabled = true;
        saveAllBtn.textContent = 'Saving 0/' + startCount + '…';
        var failed = [];
        var savedCount = 0;
        function attempt(entry, retriesLeft) {
          var payload = { _row: entry.ref._row || 0, row: {} };
          entry.headers.filter(Boolean).forEach(function(h) { payload.row[h] = entry.ref[h]; });
          return AG_ADMIN.api(entry.saveAction, payload).then(function(resp) {
            if (resp && resp._row) entry.ref._row = resp._row;
            return resp;
          }).catch(function(err) {
            /* One retry on transient errors. Apps Script /exec sometimes
               502s briefly under cold-start; the second attempt almost
               always succeeds. */
            if (retriesLeft > 0) {
              return new Promise(function(res) { setTimeout(res, 800); })
                .then(function() { return attempt(entry, retriesLeft - 1); });
            }
            throw err;
          });
        }
        function saveOne(entry) {
          return attempt(entry, 1).then(function() {
            markClean(entry.sheetKey, entry.ref);
            savedCount++;
            saveAllBtn.textContent = 'Saving ' + savedCount + '/' + startCount + '…';
            var tr = findRowTr_(entry.ref);
            if (tr) {
              tr.style.background = '#e6ffe6';
              setTimeout(function() {
                if (tr.style.background === 'rgb(230, 255, 230)') tr.style.background = '';
              }, 1200);
            }
          }).catch(function(e) {
            failed.push({ entry: entry, error: e });
          });
        }
        Promise.all(entries.map(saveOne)).then(function() {
          saveAllBtn.disabled = false;
          if (failed.length) {
            saveAllBtn.textContent = '✗ ' + failed.length + ' failed, retry?';
            saveAllBtn.style.background = '#c8102e';
            alert(failed.length + ' row(s) failed to save:\n' + failed.map(function(f) { return ' • ' + f.entry.sheetKey + ' row ' + (f.entry.ref._row || 'new') + ': ' + (f.error.message || f.error); }).join('\n'));
            return;
          }
          var ts = new Date();
          var hh = String(ts.getHours()).padStart(2, '0');
          var mm = String(ts.getMinutes()).padStart(2, '0');
          saveAllBtn.textContent = '✓ Saved ' + savedCount + ' at ' + hh + ':' + mm;
          saveAllBtn.style.background = '#1a6a1a';
          /* Full refresh once everything's saved so the UI is the source
             of truth from server (new rows get their _row numbers, delete
             buttons appear, ordering reflects display_order, etc.). */
          AG_ADMIN.api('ui_bootstrap', {}).then(function(p) {
            state.payload = p;
            setTimeout(function() {
              saveAllBtn.style.background = '';
              renderTab();
            }, 1200);
          }).catch(function() {
            /* Even if bootstrap fails, the save succeeded, operator can
               manually refresh. Just clear the styling. */
            setTimeout(function() { saveAllBtn.style.background = ''; refreshDirtyUi(); }, 1200);
          });
        });
      });
    }
    refreshDirtyUi();
    tbody.querySelectorAll('[data-delete]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(btn.dataset.delete, 10);
        var r = rows[idx];
        if (!r._row) return;
        var label = btn.dataset.deleteLabel || 'this row';
        if (!confirm('Delete ' + label + '?\n\nThis cannot be undone. Snapshot the config (Tools tab) first if unsure.')) return;
        btn.disabled = true;
        AG_ADMIN.api(deleteAction, { _row: r._row }).then(function() {
          if (ownerKey) markClean(ownerKey, r);
          rows.splice(idx, 1);
          paintTableRows(rows, headers, saveAction, deleteAction, ownerKey);
        }).catch(function(e) {
          alert('Delete failed: ' + (e.message || e));
          btn.disabled = false;
        });
      });
    });
  }

  /* ─── Config tab (key/value rows) ─────────────────────────── */
  function renderConfig(root) {
    var cfg = state.payload.config || {};
    var cs = state.payload.carrier_service || {};
    var skuSummary = state.payload.sku_carrier_summary || { total_skus: 0, tagged: 0, fallback: 0, last_synced_at: null };
    root.innerHTML = `
      <h2 class="ag-h2">Config</h2>

      <div style="background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:6px;padding:14px;margin:12px 0;">
        <h3 style="margin:0 0 8px 0;">SKU → carrier sync</h3>
        <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;">
          <div style="display:flex;gap:16px;font-size:12px;flex-wrap:wrap;">
            <div><strong>${skuSummary.total_skus}</strong> SKUs total</div>
            <div style="color:#2a7a2a;"><strong>${skuSummary.tagged}</strong> tagged <code>carrier:&lt;id&gt;</code></div>
            <div class="ag-muted"><strong>${skuSummary.fallback}</strong> fallback to <code>${esc(cfg.default_carrier_id ? cfg.default_carrier_id.value : 'default')}</code></div>
            ${skuSummary.last_synced_at ? `<div class="ag-muted">last synced ${esc(String(skuSummary.last_synced_at).slice(0, 19))}</div>` : '<div class="ag-muted">never synced</div>'}
          </div>
          <button class="ag-btn secondary" id="ship-sync-skus" style="font-size:12px;">Re-sync now</button>
        </div>
        <p class="ag-muted" style="font-size:11px;margin:8px 0 0 0;">Walks Shopify products, reads <code>carrier:&lt;id&gt;</code> tags, populates Shipping_Sku_Carriers. Auto-runs hourly via installed trigger. Tag products from the inventory dashboard or Shopify admin and re-sync to pick them up immediately.</p>
      </div>

      <div style="background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:6px;padding:14px;margin:12px 0;">
        <h3 style="margin:0 0 8px 0;">Carrier service status</h3>
        ${cs.registered ? `
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <div>
              <div><strong>${esc(cs.name)}</strong> · id <code>${esc(cs.id)}</code></div>
              <div class="ag-muted" style="font-size:11px;margin-top:2px;">${esc(cs.callback_url)}</div>
              <div style="margin-top:6px;">
                <span class="ag-pill ${cs.active ? 'ok' : 'mute'}">${cs.active ? 'ACTIVE, customers see rates' : 'INACTIVE, rates hidden'}</span>
              </div>
            </div>
            <button class="ag-btn ${cs.active ? 'secondary' : 'ag-btn-primary'}" id="cs-toggle">${cs.active ? 'Deactivate' : 'Activate'}</button>
          </div>
        ` : `<div class="ag-empty">Not registered. Run registerCarrierService() in the Apps Script editor.</div>`}
      </div>

      <h3>Key / value config</h3>
      <table class="ag-table" style="font-size:13px;"><thead><tr><th>Key</th><th>Value</th><th>Notes</th><th></th></tr></thead><tbody>
        ${Object.keys(cfg).map(function(k) {
          var row = cfg[k];
          return `<tr><td><code>${esc(k)}</code></td>
            <td><input class="ag-input" data-cfg-key="${esc(k)}" data-cfg-field="value" value="${esc(row.value)}"></td>
            <td><input class="ag-input" data-cfg-key="${esc(k)}" data-cfg-field="notes" value="${esc(row.notes || '')}"></td>
            <td><button class="ag-btn secondary" data-cfg-save="${esc(k)}" style="padding:3px 8px;font-size:11px;">Save</button></td>
          </tr>`;
        }).join('')}
      </tbody></table>
    `;
    var syncBtn = $('ship-sync-skus');
    if (syncBtn) {
      syncBtn.addEventListener('click', function() {
        syncBtn.disabled = true; syncBtn.textContent = 'Syncing…';
        AG_ADMIN.api('ui_sync_sku_carriers', {}).then(function(r) {
          syncBtn.textContent = '✓ ' + (r.tagged || 0) + ' tagged · ' + (r.untagged || 0) + ' fallback (' + (r.duration_ms || 0) + 'ms)';
          setTimeout(function() { refresh(); }, 1200);
        }).catch(function(e) {
          alert('Sync failed: ' + (e.message || e));
          syncBtn.disabled = false; syncBtn.textContent = 'Re-sync now';
        });
      });
    }
    var csBtn = $('cs-toggle');
    if (csBtn) {
      csBtn.addEventListener('click', function() {
        var nextActive = !cs.active;
        if (nextActive && !confirm('Activate? Customers WILL see AG Shipping rates at checkout immediately.')) return;
        csBtn.disabled = true;
        AG_ADMIN.api('ui_set_carrier_service_active', { active: nextActive }).then(function() {
          refresh();
        }).catch(function(e) {
          alert('Failed: ' + (e.message || e));
          csBtn.disabled = false;
        });
      });
    }
    root.querySelectorAll('[data-cfg-key]').forEach(function(input) {
      input.addEventListener('input', function(e) {
        var k = e.target.dataset.cfgKey;
        var field = e.target.dataset.cfgField;
        if (!cfg[k]) cfg[k] = { value: '', notes: '' };
        cfg[k][field] = e.target.value;
      });
    });
    root.querySelectorAll('[data-cfg-save]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var k = btn.dataset.cfgSave;
        var row = cfg[k];
        btn.disabled = true; btn.textContent = '…';
        AG_ADMIN.api('ui_save_config_row', { key: k, value: row.value, notes: row.notes || '' }).then(function() {
          btn.textContent = '✓';
          setTimeout(function() { btn.disabled = false; btn.textContent = 'Save'; }, 1200);
        }).catch(function(e) {
          btn.disabled = false; btn.textContent = 'Save';
          alert('Save failed: ' + (e.message || e));
        });
      });
    });
  }

  /* ─── Product carriers tab ────────────────────────────────────
     Search Shopify products by SKU or title, see their current
     carrier:* tag assignments, toggle multi-carrier checkboxes per
     product, save back via productUpdate. Skips the trip to the
     Shopify admin → product → tags screen. */
  var productCarriersState = {
    query: '',
    results: [],
    edits: {},     // product_id → { carrier_id → bool } pending edit
    selected: {},  // product_id → true (row checkbox state for bulk)
    loaded: false, // tracks whether we've fetched at least once
    hasNextPage: false,
    endCursor: null,
    expanded: {},  // product_id → true (row is showing variants)
    variantData: {} // product_id → { variants[], loading, error }
  };
  function renderProductCarriers(root) {
    var carriers = ((state.payload && state.payload.carriers && state.payload.carriers.rows) || [])
      .filter(function(c) { return c.carrier_id && c.enabled !== false; });
    root.innerHTML = `
      <h2 class="ag-h2">Product carriers</h2>
      <p class="ag-muted" style="font-size:12px;">Assign carriers to products. Saves write back to Shopify product tags as <code>carrier:&lt;id&gt;</code>. ${carriers.length} carrier${carriers.length === 1 ? '' : 's'} available.</p>
      <div style="display:flex;gap:8px;align-items:center;margin:12px 0;flex-wrap:wrap;">
        <input class="ag-input" id="pcar-q" placeholder="Filter by SKU or title (live-search; clear to show all)" value="${esc(productCarriersState.query)}" style="flex:1;max-width:520px;">
        <button class="ag-btn secondary" id="pcar-refresh" style="font-size:12px;">Refresh</button>
        <span class="ag-muted" id="pcar-status" style="font-size:11px;"></span>
      </div>
      <div id="pcar-bulk-bar" style="display:none;background:#fff5f5;border:1px solid #f0bdbd;padding:10px 14px;border-radius:6px;margin-bottom:10px;align-items:center;gap:12px;flex-wrap:wrap;">
        <strong id="pcar-bulk-count" style="font-size:13px;">0 selected</strong>
        <select class="ag-select" id="pcar-bulk-mode" style="font-size:12px;padding:4px 8px;">
          <option value="add" selected>Add to existing</option>
          <option value="replace">Replace existing</option>
          <option value="remove">Remove from existing</option>
        </select>
        <div id="pcar-bulk-carriers" style="display:flex;gap:8px;flex-wrap:wrap;"></div>
        <button class="ag-btn ag-btn-primary" id="pcar-bulk-apply" style="font-size:12px;">Apply</button>
        <button class="ag-btn secondary" id="pcar-bulk-clear" style="font-size:11px;">Clear selection</button>
      </div>
      <div id="pcar-results"><div class="ag-empty" style="padding:24px;"><span class="ag-spinner"></span> Loading products…</div></div>
    `;
    var searchTimer = null;
    $('pcar-q').addEventListener('input', function() {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(function() { runProductSearch(carriers); }, 350);
    });
    $('pcar-refresh').addEventListener('click', function() { runProductSearch(carriers); });
    /* Bulk carrier picker. */
    document.getElementById('pcar-bulk-carriers').innerHTML = carriers.map(function(c) {
      var cid = String(c.carrier_id).toLowerCase();
      return '<label style="font-size:12px;display:inline-flex;align-items:center;gap:4px;">' +
        '<input type="checkbox" data-pcar-bulk-cid="' + esc(cid) + '"> ' + esc(c.label || cid) +
        '</label>';
    }).join('');
    $('pcar-bulk-apply').addEventListener('click', function() { runBulkApply(carriers); });
    $('pcar-bulk-clear').addEventListener('click', function() {
      productCarriersState.selected = {};
      paintProductCarrierResults(carriers);
    });
    /* Auto-load on first tab open. */
    if (!productCarriersState.loaded) runProductSearch(carriers);
    else paintProductCarrierResults(carriers);
  }
  function runProductSearch(carriers, append) {
    var q = $('pcar-q').value.trim();
    if (!append) {
      productCarriersState.query = q;
      productCarriersState.endCursor = null;
    }
    var after = append ? productCarriersState.endCursor : null;
    $('pcar-status').textContent = (append ? 'Loading more…' : (q ? 'Searching for ' + q + '…' : 'Loading…'));
    AG_ADMIN.api('ui_search_products_with_carriers', { q: q, after: after }).then(function(r) {
      var fresh = r.products || [];
      if (append) {
        productCarriersState.results = productCarriersState.results.concat(fresh);
      } else {
        productCarriersState.results = fresh;
        productCarriersState.edits = {};
      }
      productCarriersState.loaded = true;
      productCarriersState.hasNextPage = !!r.has_next_page;
      productCarriersState.endCursor = r.end_cursor || null;
      var n = productCarriersState.results.length;
      $('pcar-status').textContent = n + ' product' + (n === 1 ? '' : 's') + (q ? ' matched' : ' loaded') + (productCarriersState.hasNextPage ? ' (more available)' : '');
      paintProductCarrierResults(carriers);
    }).catch(function(e) {
      $('pcar-status').textContent = 'Failed: ' + (e.message || e);
    });
  }
  function selectedProductIds() {
    return Object.keys(productCarriersState.selected).filter(function(id) { return productCarriersState.selected[id]; });
  }
  function refreshBulkBar() {
    var bar = $('pcar-bulk-bar');
    var n = selectedProductIds().length;
    if (!bar) return;
    if (n > 0) {
      bar.style.display = 'flex';
      $('pcar-bulk-count').textContent = n + ' selected';
    } else {
      bar.style.display = 'none';
    }
  }
  function runBulkApply(carriers) {
    var ids = selectedProductIds();
    if (!ids.length) return;
    var mode = $('pcar-bulk-mode').value || 'add';
    var checks = document.querySelectorAll('[data-pcar-bulk-cid]');
    var carrierIds = Array.prototype.filter.call(checks, function(c) { return c.checked; })
      .map(function(c) { return c.dataset.pcarBulkCid; });
    if (!carrierIds.length) { alert('Tick at least one carrier in the bar.'); return; }
    var verb = mode === 'replace' ? 'REPLACE existing carrier:* tags with' : (mode === 'remove' ? 'REMOVE' : 'ADD');
    if (!confirm(verb + ' ' + carrierIds.length + ' carrier(s) ' + (mode === 'remove' ? 'from' : (mode === 'replace' ? 'on' : 'to')) + ' ' + ids.length + ' product(s)?')) return;
    var btn = $('pcar-bulk-apply');
    /* Estimate: add/remove ~0.7s/product (single mutation), replace ~1.5s
       (read + update). Includes 200ms throttle gap. */
    var perProductS = mode === 'replace' ? 1.5 : 0.7;
    btn.disabled = true; btn.textContent = 'Applying… (~' + Math.ceil(ids.length * perProductS) + 's)';
    AG_ADMIN.api('ui_bulk_set_product_carriers', {
      product_ids: ids, carrier_ids: carrierIds, mode: mode
    }).then(function(r) {
      var failedIds = (r.results || []).filter(function(x) { return !x.ok; }).map(function(x) { return x.product_id; });
      var msg = 'Updated ' + (r.updated || 0) + ' product(s)' + (r.failed ? '; ' + r.failed + ' failed' : '') + '.';
      if (failedIds.length) {
        msg += '\n\nRetry the ' + failedIds.length + ' failed product(s)?';
        if (confirm(msg)) {
          btn.textContent = 'Retrying ' + failedIds.length + '…';
          AG_ADMIN.api('ui_bulk_set_product_carriers', {
            product_ids: failedIds, carrier_ids: carrierIds, mode: mode
          }).then(function(r2) {
            alert('Retry: updated ' + (r2.updated || 0) + (r2.failed ? '; ' + r2.failed + ' still failed' : '') + '.');
            productCarriersState.selected = {};
            runProductSearch(carriers);
          }).catch(function(e) {
            alert('Retry failed: ' + (e.message || e));
            btn.disabled = false; btn.textContent = 'Apply';
          });
          return;
        }
      } else {
        alert(msg);
      }
      productCarriersState.selected = {};
      runProductSearch(carriers); // refresh to pull new tag state
    }).catch(function(e) {
      alert('Bulk apply failed: ' + (e.message || e));
      btn.disabled = false; btn.textContent = 'Apply';
    });
  }
  var PCAR_DONE_KEY = 'ag-shipping-pcar-done:v2';
  function readDoneSet_() {
    try { return JSON.parse(localStorage.getItem(PCAR_DONE_KEY) || '{}') || {}; }
    catch (_) { return {}; }
  }
  function writeDoneSet_(set) {
    try { localStorage.setItem(PCAR_DONE_KEY, JSON.stringify(set)); } catch (_) {}
  }
  /* Fingerprint a product's IDENTITY, only what the operator can't
     control. Tag-based flags (carriers, combinable, external) are
     EXCLUDED because the operator changing those is the whole reason
     they're on this tab; auto-evicting on every checkbox click would
     defeat the purpose. The fingerprint mismatches when:
       - Product handle changes (renamed in Shopify)
       - Any variant SKU added / removed / renamed
     i.e. a structural change that means "this isn't the same product
     I verified earlier, re-check it". */
  function productDoneFingerprint_(p) {
    if (!p) return '';
    var skus = (p.variants || []).map(function(v) { return v.sku || ''; }).sort().join('|');
    return [
      p.handle || '',
      skus
    ].join('::');
  }
  /* Filter the done set on every paint, dropping any entry whose
     fingerprint no longer matches the live product data. Persists the
     cleaned set back so the eviction is durable. */
  function activeDoneSet_(allRows) {
    var raw = readDoneSet_();
    var cleaned = {};
    var evicted = 0;
    (allRows || []).forEach(function(p) {
      var entry = raw[p.product_id];
      if (!entry) return;
      var current = productDoneFingerprint_(p);
      /* Legacy v1 entries (just a timestamp number) → treat as valid
         until next mark-done overwrites them. */
      if (typeof entry === 'number') {
        cleaned[p.product_id] = { marked_at: entry, fingerprint: current };
        return;
      }
      if (entry.fingerprint && entry.fingerprint === current) {
        cleaned[p.product_id] = entry;
      } else if (entry.fingerprint && entry.fingerprint.indexOf(current + '::') === 0) {
        /* Old wide-form fingerprint (handle:skus:carriers:...), the
           new narrow form (handle:skus) is a prefix. Accept it as
           still valid and silently upgrade to the new shape. */
        cleaned[p.product_id] = { marked_at: entry.marked_at, fingerprint: current };
      } else {
        evicted++;
      }
    });
    /* Also keep entries for products NOT in the current rows array,
       those just aren't on screen, no reason to drop them yet. */
    Object.keys(raw).forEach(function(pid) {
      if (cleaned[pid]) return;
      var stillInRows = (allRows || []).some(function(p) { return p.product_id === pid; });
      if (!stillInRows) cleaned[pid] = raw[pid];
    });
    if (evicted > 0) writeDoneSet_(cleaned);
    return { set: cleaned, evicted: evicted };
  }
  function paintProductCarrierResults(carriers) {
    var box = $('pcar-results');
    if (!box) return;
    /* Skip products that can't possibly affect a live checkout:
         - ARCHIVED (Shopify status), not buyable
         - No SKU on any variant, sync skips them anyway
       Hide silently with counts so the operator isn't wading through
       inert entries. */
    var allRowsRaw = productCarriersState.results;
    var noSkuCount = 0;
    var archivedCount = 0;
    var allRows = allRowsRaw.filter(function(p) {
      if (String(p.status || '').toUpperCase() === 'ARCHIVED') { archivedCount++; return false; }
      var hasSku = (p.variants || []).some(function(v) { return v.sku && v.sku.trim(); });
      if (!hasSku) noSkuCount++;
      return hasSku;
    });
    var doneState = activeDoneSet_(allRows);
    var done = doneState.set;
    var evictedNote = doneState.evicted;
    var hasQuery = !!(productCarriersState.query && productCarriersState.query.length);
    var showHidden = !!productCarriersState.showHidden || hasQuery;
    var hiddenCount = allRows.filter(function(p) { return done[p.product_id]; }).length;
    var rows = showHidden ? allRows : allRows.filter(function(p) { return !done[p.product_id]; });
    if (!rows.length && !allRows.length) {
      box.innerHTML = '<div class="ag-empty" style="padding:24px;">No products.</div>';
      refreshBulkBar();
      return;
    }
    /* Determine if "select all" should be ticked. */
    var visibleSelectedCount = rows.filter(function(p) { return productCarriersState.selected[p.product_id]; }).length;
    var allSelected = visibleSelectedCount === rows.length;
    var thSticky = 'position:sticky;top:0;background:#fff;z-index:3;box-shadow:0 1px 0 #e6e6e6;';
    /* Done toggle + count above the table. Shown when there's at least
       one hidden product; search auto-shows hidden too so nothing is
       ever truly out of reach. */
    var doneToggleHtml = '';
    if (hiddenCount > 0 || evictedNote > 0 || noSkuCount > 0 || archivedCount > 0) {
      doneToggleHtml = '<div style="display:flex;align-items:center;gap:10px;margin:6px 0 8px;font-size:12px;color:#555;flex-wrap:wrap;">' +
        (hiddenCount > 0 ? '<span><strong>' + hiddenCount + '</strong> product(s) marked done · hidden</span>' : '') +
        (archivedCount > 0 ? '<span class="ag-muted"><strong>' + archivedCount + '</strong> hidden (archived)</span>' : '') +
        (noSkuCount > 0 ? '<span class="ag-muted"><strong>' + noSkuCount + '</strong> hidden (no SKUs)</span>' : '') +
        (evictedNote > 0 ? '<span style="color:#c8102e;"><strong>' + evictedNote + '</strong> auto-unmarked (Shopify data changed)</span>' : '') +
        (hasQuery
          ? '<span class="ag-muted" style="font-size:11px;">(search shows hidden too)</span>'
          : (hiddenCount > 0 ? '<button class="ag-btn secondary" id="pcar-toggle-hidden" style="padding:3px 10px;font-size:11px;">' + (productCarriersState.showHidden ? 'Hide done again' : 'Show done') + '</button>' : '')
        ) +
        '</div>';
    }
    box.innerHTML = doneToggleHtml +
      '<table class="ag-table" style="font-size:13px;"><thead><tr>' +
      '<th style="width:32px;' + thSticky + '"><input type="checkbox" id="pcar-select-all" ' + (allSelected ? 'checked' : '') + ' title="Select all visible"></th>' +
      '<th style="' + thSticky + '">Product</th>' +
      carriers.map(function(c) { return '<th style="text-align:center;font-size:11px;' + thSticky + '">' + esc(c.label || c.carrier_id) + '<br><code style="font-size:10px;color:#888;">' + esc(c.carrier_id) + '</code></th>'; }).join('') +
      '<th style="text-align:center;font-size:11px;width:90px;' + thSticky + '" title="Tick to allow this product to share a parcel with other combinable items, up to the carrier\'s combine_max_kg.">Combinable<br><code style="font-size:10px;color:#888;">tag</code></th>' +
      '<th style="text-align:center;font-size:11px;width:100px;' + thSticky + '" title="Drop-ship / partner fulfilment. AG calc excludes this product entirely, no weight, no carrier, no parcel count.">External<br><code style="font-size:10px;color:#888;">tag</code></th>' +
      '<th style="' + thSticky + '">Done?</th></tr></thead><tbody>' +
      rows.map(function(p, rowIdx) {
        var skuLine = (p.variants || []).map(function(v) { return v.sku; }).filter(Boolean).join(', ');
        var pendingEdit = productCarriersState.edits[p.product_id];
        var assigned = pendingEdit || (p.assigned_carriers || []).reduce(function(m, c) { m[c] = true; return m; }, {});
        var changed = !!pendingEdit;
        var isSelected = !!productCarriersState.selected[p.product_id];
        var isExpanded = !!productCarriersState.expanded[p.product_id];
        var variantRowsHtml = '';
        if (isExpanded) {
          var loaded = productCarriersState.variantData[p.product_id];
          if (!loaded || loaded.loading) {
            variantRowsHtml = '<tr><td colspan="' + (carriers.length + 3) + '" class="ag-muted" style="padding:14px 24px;font-size:12px;"><span class="ag-spinner"></span> Loading variants…</td></tr>';
          } else if (loaded.error) {
            variantRowsHtml = '<tr><td colspan="' + (carriers.length + 3) + '" class="ag-empty" style="color:#c00;">Failed to load: ' + esc(loaded.error) + '</td></tr>';
          } else {
            variantRowsHtml = (loaded.variants || []).map(function(v, vi) {
              /* override_carrier_ids is the explicit override list
                 (empty → inherits from product). Falls back to legacy
                 single override_carrier_id for back-compat. */
              var overrideList = Array.isArray(v.override_carrier_ids) ? v.override_carrier_ids.slice() : [];
              if (!overrideList.length && v.override_carrier_id) overrideList = [v.override_carrier_id];
              var hasOverride = overrideList.length > 0;
              /* Inherited from product when no override is set. */
              var inheritedList = hasOverride ? [] : (p.assigned_carriers || []);
              var overrideMap = {};
              overrideList.forEach(function(x) { overrideMap[String(x).toLowerCase()] = true; });
              var inheritedMap = {};
              inheritedList.forEach(function(x) { inheritedMap[String(x).toLowerCase()] = true; });
              return '<tr style="background:#fafafa;">' +
                '<td></td>' +
                '<td style="padding-left:32px;font-size:12px;">' +
                  '<div style="font-family:monospace;font-weight:600;">' + esc(v.sku || '(no SKU)') + '</div>' +
                  '<div class="ag-muted" style="font-size:11px;">' + esc(v.title || 'Default') + ' · ' + (v.grams ? (v.grams / 1000).toFixed(2) + ' kg' : 'no weight') + '</div>' +
                '</td>' +
                carriers.map(function(c) {
                  var cid = String(c.carrier_id).toLowerCase();
                  /* Multi-select: checkboxes per carrier per variant.
                     Inherited ticks render with reduced opacity so the
                     operator sees "this is what's currently in effect"
                     vs an explicit override. */
                  var checked = hasOverride ? overrideMap[cid] : inheritedMap[cid];
                  var faded = !hasOverride && inheritedMap[cid];
                  return '<td style="text-align:center;">' +
                    '<input type="checkbox" data-pcar-var-cid="' + esc(cid) + '" data-pcar-var-row="' + rowIdx + '" data-pcar-var-sku="' + esc(v.sku) + '" data-pcar-var-pid="' + esc(p.product_id) + '" data-pcar-var-vid="' + esc(v.variant_id) + '" data-pcar-var-vtitle="' + esc(v.title || '') + '"' +
                    (checked ? ' checked' : '') +
                    (faded ? ' style="opacity:0.45;" title="Inherited from product, click to start a variant override"' : ' title="Tick to include this carrier on this variant"') +
                    '></td>';
                }).join('') +
                '<td style="text-align:center;"></td>' + // Combinable column spacer (variants inherit from product)
                '<td style="text-align:center;"></td>' + // External column spacer (variants inherit from product)
                '<td>' +
                  (hasOverride
                    ? '<button class="ag-btn secondary" data-pcar-var-clear="' + esc(v.sku) + '" data-pcar-var-row="' + rowIdx + '" data-pcar-var-pid="' + esc(p.product_id) + '" style="padding:2px 8px;font-size:10px;" title="Remove override, variant goes back to inheriting from product">× clear override</button>'
                    : '<span class="ag-muted" style="font-size:10px;">inherits</span>'
                  ) +
                '</td>' +
              '</tr>';
            }).join('');
          }
        }
        return '<tr data-row="' + rowIdx + '"' + (isSelected ? ' style="background:rgba(200, 16, 46,0.04);"' : '') + '>' +
          '<td><input type="checkbox" class="pcar-row-check" data-pcar-pid="' + esc(p.product_id) + '" ' + (isSelected ? 'checked' : '') + '></td>' +
          '<td><div style="display:flex;align-items:center;gap:6px;">' +
            '<button class="ag-btn secondary" data-pcar-expand="' + rowIdx + '" style="padding:0 6px;font-size:11px;width:24px;height:24px;line-height:1;" title="' + (isExpanded ? 'Collapse' : 'Expand variants') + '">' + (isExpanded ? '▾' : '▸') + '</button>' +
            '<div><div style="font-weight:600;">' + esc(p.title) + '</div>' +
            (skuLine ? '<div class="ag-muted" style="font-size:11px;font-family:monospace;">' + esc(skuLine) + '</div>' : '') +
            '</div></div>' +
          '</td>' +
          carriers.map(function(c) {
            var cid = String(c.carrier_id).toLowerCase();
            return '<td style="text-align:center;"><input type="checkbox" data-pcar-cid="' + esc(cid) + '" data-pcar-row="' + rowIdx + '" ' + (assigned[cid] ? 'checked' : '') + '></td>';
          }).join('') +
          '<td style="text-align:center;"><input type="checkbox" data-pcar-combinable="' + rowIdx + '" ' + (p.combinable ? 'checked' : '') + ' title="Toggle combinable tag"></td>' +
          '<td style="text-align:center;">' +
            '<input type="checkbox" data-pcar-external="' + rowIdx + '" ' + (p.external_fulfilment ? 'checked' : '') + (p.shopify_collective ? ' disabled' : '') + ' title="' + (p.shopify_collective ? 'Auto-set by Shopify Collective tag, supplier ships directly' : 'Toggle external-fulfilment tag') + '">' +
            (p.shopify_collective ? '<div class="ag-muted" style="font-size:9px;line-height:1.1;margin-top:2px;color:#888;">Collective</div>' : '') +
          '</td>' +
          '<td style="text-align:center;white-space:nowrap;">' +
            (done[p.product_id]
              ? '<button class="ag-btn secondary" data-pcar-done="' + esc(p.product_id) + '" data-pcar-done-state="undo" style="padding:3px 10px;font-size:11px;" title="Unmark, bring this product back into the main list">Undo</button>'
              : '<button class="ag-btn ag-btn-primary" data-pcar-done="' + esc(p.product_id) + '" data-pcar-done-state="mark" style="padding:3px 10px;font-size:11px;" title="Mark as done, saves pending edits and hides this product from the main list">Mark done</button>'
            ) +
            ' <button class="ag-btn ' + (changed ? 'ag-btn-primary' : 'secondary') + '" data-pcar-save="' + rowIdx + '" style="padding:3px 8px;font-size:11px;">' + (changed ? 'Save' : 'Saved') + '</button>' +
          '</td>' +
        '</tr>' + variantRowsHtml;
      }).join('') +
      '</tbody></table>' +
      (productCarriersState.hasNextPage
        ? '<div style="margin-top:12px;text-align:center;"><button class="ag-btn ag-btn-primary" id="pcar-load-more" style="font-size:12px;">Load more (' + rows.length + ' loaded)</button></div>'
        : (rows.length >= 50 ? '<p class="ag-muted" style="text-align:center;font-size:11px;margin-top:12px;">End of catalogue · ' + rows.length + ' product(s) loaded.</p>' : ''));
    var loadMore = $('pcar-load-more');
    if (loadMore) loadMore.addEventListener('click', function() { runProductSearch(carriers, true); });
    /* Per-carrier checkbox handlers. */
    box.querySelectorAll('[data-pcar-cid]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var rowIdx = parseInt(cb.dataset.pcarRow, 10);
        var cid = cb.dataset.pcarCid;
        var product = rows[rowIdx]; // filtered visible array; matches data-pcar-row indices
        var edit = productCarriersState.edits[product.product_id];
        if (!edit) {
          edit = {};
          (product.assigned_carriers || []).forEach(function(c) { edit[c] = true; });
          productCarriersState.edits[product.product_id] = edit;
        }
        edit[cid] = cb.checked;
        if (!cb.checked) delete edit[cid];
        paintProductCarrierResults(carriers);
      });
    });
    /* Combinable checkbox. Saves immediately (single round-trip, no
       row-save button needed for this flag). Optimistic UI update +
       silent revert on error. */
    box.querySelectorAll('[data-pcar-combinable]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var rowIdx = parseInt(cb.dataset.pcarCombinable, 10);
        var product = rows[rowIdx];
        var prev = !!product.combinable;
        var next = cb.checked;
        product.combinable = next;
        AG_ADMIN.api('ui_set_product_combinable', {
          product_id: product.product_id, combinable: next
        }).catch(function(e) {
          product.combinable = prev;
          cb.checked = prev;
          alert('Could not update combinable: ' + (e.message || e));
        });
      });
    });
    box.querySelectorAll('[data-pcar-external]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var rowIdx = parseInt(cb.dataset.pcarExternal, 10);
        var product = rows[rowIdx];
        var prev = !!product.external_fulfilment;
        var next = cb.checked;
        product.external_fulfilment = next;
        AG_ADMIN.api('ui_set_product_external_fulfilment', {
          product_id: product.product_id, external_fulfilment: next
        }).catch(function(e) {
          product.external_fulfilment = prev;
          cb.checked = prev;
          alert('Could not update external fulfilment: ' + (e.message || e));
        });
      });
    });
    /* Per-row save. */
    /* Done toggle, persists product IDs in localStorage so the operator
       can hide rows they've finished without leaving a trail in Shopify
       tags. Search auto-shows hidden rows so they can always be found. */
    box.querySelectorAll('[data-pcar-done]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var pid = btn.dataset.pcarDone;
        var state = btn.dataset.pcarDoneState; // 'mark' | 'undo'
        var d = readDoneSet_();
        function applyAndRepaint() {
          if (state === 'mark') {
            /* Snapshot the product's fingerprint so we can detect any
               Shopify-side change later and auto-unmark. */
            var product = rows.find ? rows.find(function(p) { return p.product_id === pid; }) : null;
            d[pid] = {
              marked_at: Date.now(),
              fingerprint: product ? productDoneFingerprint_(product) : ''
            };
          } else {
            delete d[pid];
          }
          writeDoneSet_(d);
          paintProductCarrierResults(carriers);
        }
        /* If marking done AND there are pending carrier-checkbox edits
           for this product, save them BEFORE hiding so the operator's
           tick changes don't disappear with the row. */
        var pendingEdit = productCarriersState.edits[pid];
        if (state === 'mark' && pendingEdit) {
          btn.disabled = true;
          btn.textContent = 'Saving…';
          var ids = Object.keys(pendingEdit).filter(function(k) { return pendingEdit[k]; });
          AG_ADMIN.api('ui_set_product_carriers', {
            product_id: pid, carrier_ids: ids, mode: 'replace'
          }).then(function() {
            var product = rows.find ? rows.find(function(p) { return p.product_id === pid; }) : null;
            if (product) product.assigned_carriers = ids;
            delete productCarriersState.edits[pid];
            applyAndRepaint();
          }).catch(function(e) {
            btn.disabled = false;
            btn.textContent = 'Mark done';
            alert('Save failed: not marked done: ' + (e.message || e));
          });
        } else {
          applyAndRepaint();
        }
      });
    });
    var toggleBtn = document.getElementById('pcar-toggle-hidden');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function() {
        productCarriersState.showHidden = !productCarriersState.showHidden;
        paintProductCarrierResults(carriers);
      });
    }
    box.querySelectorAll('[data-pcar-save]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var rowIdx = parseInt(btn.dataset.pcarSave, 10);
        var product = rows[rowIdx]; // filtered visible array; matches data-pcar-row indices
        var edit = productCarriersState.edits[product.product_id];
        if (!edit) return;
        var ids = Object.keys(edit).filter(function(k) { return edit[k]; });
        btn.disabled = true; btn.textContent = '…';
        AG_ADMIN.api('ui_set_product_carriers', {
          product_id: product.product_id, carrier_ids: ids, mode: 'replace'
        }).then(function() {
          product.assigned_carriers = ids;
          delete productCarriersState.edits[product.product_id];
          paintProductCarrierResults(carriers);
        }).catch(function(e) {
          alert('Save failed: ' + (e.message || e));
          btn.disabled = false; btn.textContent = 'Save';
        });
      });
    });
    /* Row select checkboxes. */
    box.querySelectorAll('.pcar-row-check').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var pid = cb.dataset.pcarPid;
        if (cb.checked) productCarriersState.selected[pid] = true;
        else delete productCarriersState.selected[pid];
        paintProductCarrierResults(carriers);
        refreshBulkBar();
      });
    });
    /* Expand / collapse. Fetch variants lazily on first expand. */
    box.querySelectorAll('[data-pcar-expand]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var rowIdx = parseInt(btn.dataset.pcarExpand, 10);
        var product = rows[rowIdx]; // filtered visible array; matches data-pcar-row indices
        var pid = product.product_id;
        var isOpen = !!productCarriersState.expanded[pid];
        if (isOpen) {
          delete productCarriersState.expanded[pid];
          paintProductCarrierResults(carriers);
          return;
        }
        productCarriersState.expanded[pid] = true;
        if (!productCarriersState.variantData[pid]) {
          productCarriersState.variantData[pid] = { loading: true };
          paintProductCarrierResults(carriers);
          AG_ADMIN.api('ui_get_product_variants', { product_id: pid }).then(function(r) {
            productCarriersState.variantData[pid] = { variants: r.variants || [] };
            paintProductCarrierResults(carriers);
          }).catch(function(e) {
            productCarriersState.variantData[pid] = { error: e.message || String(e) };
            paintProductCarrierResults(carriers);
          });
        } else {
          paintProductCarrierResults(carriers);
        }
      });
    });
    /* Per-variant checkbox, picking writes an override row with the
       full set of ticked carriers. Inherited (faded) ticks turn into
       the operator's explicit override the moment they click anything. */
    box.querySelectorAll('[data-pcar-var-cid]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var sku = cb.dataset.pcarVarSku;
        var pid = cb.dataset.pcarVarPid;
        var vid = cb.dataset.pcarVarVid;
        var vtitle = cb.dataset.pcarVarVtitle;
        var rowIdx = parseInt(cb.dataset.pcarVarRow, 10);
        var product = rows[rowIdx];
        /* Collect every ticked carrier in this variant row. */
        var tr = cb.closest('tr');
        var checkedIds = Array.prototype.slice.call(tr.querySelectorAll('[data-pcar-var-cid]'))
          .filter(function(x) { return x.checked; })
          .map(function(x) { return x.dataset.pcarVarCid; });
        /* If everything ends up unchecked, clear the override (delete row). */
        if (!checkedIds.length) {
          AG_ADMIN.api('ui_set_variant_carrier', { sku: sku, carrier_id: '' }).then(function() {
            var vd = productCarriersState.variantData[pid];
            if (vd && vd.variants) {
              vd.variants.forEach(function(v) {
                if (String(v.sku || '').toUpperCase() === sku.toUpperCase()) {
                  v.override_carrier_id = '';
                  v.override_carrier_ids = [];
                }
              });
            }
            paintProductCarrierResults(carriers);
          }).catch(function(e) {
            alert('Variant save failed: ' + (e.message || e));
          });
          return;
        }
        AG_ADMIN.api('ui_set_variant_carrier', {
          sku: sku, carrier_id: checkedIds.join(','), carrier_ids: checkedIds,
          product_id: pid, variant_id: vid,
          product_title: product.title, variant_title: vtitle
        }).then(function() {
          var vd = productCarriersState.variantData[pid];
          if (vd && vd.variants) {
            vd.variants.forEach(function(v) {
              if (String(v.sku || '').toUpperCase() === sku.toUpperCase()) {
                v.override_carrier_id = checkedIds[0];
                v.override_carrier_ids = checkedIds.slice();
              }
            });
          }
          paintProductCarrierResults(carriers);
        }).catch(function(e) {
          alert('Variant save failed: ' + (e.message || e));
        });
      });
    });
    box.querySelectorAll('[data-pcar-var-clear]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var sku = btn.dataset.pcarVarClear;
        var pid = btn.dataset.pcarVarPid;
        AG_ADMIN.api('ui_set_variant_carrier', { sku: sku, carrier_id: '' }).then(function() {
          var vd = productCarriersState.variantData[pid];
          if (vd && vd.variants) {
            vd.variants.forEach(function(v) {
              if (String(v.sku || '').toUpperCase() === sku.toUpperCase()) {
                v.override_carrier_id = '';
                v.override_carrier_ids = [];
              }
            });
          }
          paintProductCarrierResults(carriers);
        }).catch(function(e) {
          alert('Clear failed: ' + (e.message || e));
        });
      });
    });
    /* Select-all. */
    var selAll = $('pcar-select-all');
    if (selAll) {
      selAll.addEventListener('change', function() {
        rows.forEach(function(p) {
          if (selAll.checked) productCarriersState.selected[p.product_id] = true;
          else delete productCarriersState.selected[p.product_id];
        });
        paintProductCarrierResults(carriers);
        refreshBulkBar();
      });
    }
    refreshBulkBar();
  }

  /* ─── Logs tab ────────────────────────────────────────────── */
  function renderLogs(root) {
    root.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;">
        <div>
          <h2 class="ag-h2" style="margin:0;">Recent quote requests</h2>
        </div>
        <button class="ag-btn secondary" id="logs-refresh" style="font-size:12px;">Refresh</button>
      </div>
      <div class="ag-ship-intro">
        Every time a customer reaches checkout, Shopify asks our calculator for rates and we write a row here. Use this when someone says <em>"I was quoted €X, why?"</em>, find the row by postcode/time, the <strong>rule path</strong> column names every rate row + surcharge that fired.
      </div>
      <div id="logs-body"><div class="ag-empty"><span class="ag-spinner"></span> Loading…</div></div>
    `;
    $('logs-refresh').addEventListener('click', loadLogs);
    loadLogs();
  }
  function loadLogs() {
    AG_ADMIN.api('ui_recent_logs', { limit: 100 }).then(function(r) {
      var rows = r.rows || [];
      $('logs-body').innerHTML = rows.length ? `
        <table class="ag-table" style="font-size:12px;"><thead><tr>
          <th>Time</th><th>Zone</th><th>Postcode</th><th class="num">Weight</th><th class="num">Subtotal</th><th class="num">Quoted</th><th>Service</th><th>Rule path</th><th>Notes</th>
        </tr></thead><tbody>${rows.map(function(r) {
          return `<tr>
            <td class="ag-muted">${esc(String(r.logged_at).slice(0, 19))}</td>
            <td>${esc(r.resolved_zone || '')}</td>
            <td>${esc(r.shop_postcode || '')}</td>
            <td class="num">${(r.total_weight_kg || 0).toFixed ? r.total_weight_kg.toFixed(2) : r.total_weight_kg} kg</td>
            <td class="num">€${(parseFloat(r.subtotal_gbp) || 0).toFixed(2)}</td>
            <td class="num"><strong>${fmtMoney(r.quoted_gbp)}</strong></td>
            <td>${esc(r.quoted_service_name || '')}</td>
            <td class="ag-muted" style="font-family:monospace;font-size:10px;">${esc(r.rule_path || '')}</td>
            <td class="ag-muted">${esc(r.notes || '')}</td>
          </tr>`;
        }).join('')}</tbody></table>
      ` : '<div class="ag-empty">No quote requests logged yet. Test the calculator or place a draft order.</div>';
    }).catch(function(e) {
      $('logs-body').innerHTML = '<div class="ag-empty">Failed: ' + esc(e.message || e) + '</div>';
    });
  }
})();
